import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom, Observable, Subscription } from "rxjs";
import { map, take, tap } from "rxjs/operators";
import { encode } from "blurhash";
import { UnitService } from "./unit.service";
import * as moment from "moment-timezone";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment"
import { AngularFireDatabase, AngularFireList, AngularFireObject } from "@angular/fire/compat/database";
import { LanguageService } from "./language.service";
import { LanguagePipe } from "../pipes/language.pipe";
import { SettingService } from "./settings.service";
import { Coupon } from "../models/models";
import { ADDON_SERVING_METHOD_SET } from "../shared/delivery-methods.admin";

@Injectable({ providedIn: "root" })
export class MenuService {
    clientRef: any;
    private productTypes: ProductType[];
    private productInfoTypes: { bongCategoryTypes: ProductTypeChild[], productCategoryTypes: ProductTypeChild[] };
    private productsRef: AngularFireList<IMenuItem>;
    private customMenusRef: AngularFireList<IMenu>;
    private activeMenuRef: AngularFireObject<IMenu>;
    private currentClientUnitRoute: string;
    products: BehaviorSubject<IMenuItem[]> = new BehaviorSubject([]);
    /** Products with `isWeeklyMenuProduct === true` (indexed RTDB query; weekly menu register only). */
    weeklyMenuProducts: BehaviorSubject<IMenuItem[]> = new BehaviorSubject([]);
    cateringProducts$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    customMenus: BehaviorSubject<IMenu[]> = new BehaviorSubject([]);
    activeMenu = new BehaviorSubject<IMenu>(undefined);
    subscriptions = new Subscription();
    private currentPageSubject = new BehaviorSubject<number>(0);
    private currentRowSubject = new BehaviorSubject<number>(25);
    currentPage$ = this.currentPageSubject.asObservable();
    rowsPerPage$ = this.currentRowSubject.asObservable();
    subCategoriesGroup: BehaviorSubject<IMenuCategory[]> = new BehaviorSubject([]);
    cateringProdListColumns: BehaviorSubject<Column[]> = new BehaviorSubject([]);
    productListColumns: BehaviorSubject<Column[]> = new BehaviorSubject([]);
    productListSelectableColumns: BehaviorSubject<Column[]> = new BehaviorSubject(this.productListColumns.value);
    selectableCateringProdListColumns: BehaviorSubject<Column[]> = new BehaviorSubject(this.cateringProdListColumns.value);
    dbRefsSetup: boolean = false;
    globalSubCategoriesRef: any
    private productsOnlyRoute: string | null = null;
    private productsOnlySub: Subscription | null = null;
    /** In-memory cache for one-shot menuItem reads (e.g. legacy weekly menu entries without the flag). */
    private productFetchCache = new Map<string, IMenuItem | null>();
    TCPOSIds = [
        { label: "Dagens rätt", value: "40005" },
        { label: "Måltidspaket", value: "40006" },
        { label: "Frysta rätter", value: "40007" },
        { label: "Great Taste No Waste", value: "40008" },
        { label: "Läsk", value: "40009" },
        { label: "Glass", value: "40010" },
        { label: "Konditori", value: "40011" },
        { label: "Kaka", value: "40012" },
        { label: "Godis", value: "40013" },
        { label: "Choklad", value: "40014" },
        { label: "Frukostpaket", value: "40015" },
        { label: "Smörgås", value: "40016" },
        { label: "Yoghurt", value: "40017" },
        { label: "Grönsak", value: "40018" },
        { label: "Funktionsdryck", value: "40019" },
        { label: "Energidryck", value: "40020" },
        { label: "Öl", value: "40021" },
        { label: "Vin", value: "40022" },
        { label: "Cider", value: "40023" },
        { label: "Sprit", value: "40024" },
        { label: "Sallad", value: "40025" },
        { label: "Kolsyrat vatten", value: "40026" },
        { label: "Bars", value: "40027" },
        { label: "Wrap", value: "40028" },
        { label: "Kaffe", value: "40029" },
        { label: "Te", value: "40030" },
        { label: "Stilla vatten", value: "40031" },
        { label: "Måltidsdryck", value: "40032" },
        { label: "Alkoholfri öl", value: "40033" },
        { label: "Alkoholfritt vin", value: "40034" },
        { label: "Alkoholfri cider", value: "40035" },
        { label: "Tillval på mat", value: "40036" },
        { label: "Juice", value: "40037" },
        { label: "Konfektyr/Snacks/Tuggummi", value: "40038" },
        { label: "Frukt", value: "40039" },
        { label: "Övrigt", value: "40040" }
    ];
    cateringProduct = {
        name: '',
        cost: '',
        description: '',
        descriptionLong: '',
        imageUrl: '',
        category: null,
        dietaryTags: [],
        ecoTags: [],
        servingsPerProduct: '',
        minimumOrderQuantity: '',
        pickUpOrDelivery: [],
        economics: {
            accountingAndVAT: '',
            externalID: '',
            stockBalance: '',
        },
        enabled: true,
    };
    firstTime: boolean = true;
    private allergenCategoryTypes: string[] = [
    'MILK',
    'MILK_PROTEIN',
    'GLUTEN',
    'NUTS',
    'CRUSTACEANS',
    'EGG',
    'MOLLUSCS',
    'FISH',
    'PORK',
    'PEANUTS',
    'SESAME_SEEDS',
    'SULPHUR',
    'SOYBEANS',
    'CELERY',
    'MUSTARD',
    'LUPIN',
    'MUSHROOM',
    'ALMOND',
    'HAZELNUT',
    'WALNUT',
    'CASHEW',
    'PECAN',
    'BRAZIL_NUT',
    'PISTACHIO',
    'MACADAMIA'
  ];

    constructor(private afDatabase: AngularFireDatabase, private unitService: UnitService, private http: HttpClient, private languagePipe: LanguagePipe, private languageProvider: LanguageService, private settingsService: SettingService) {
        // Subscribe to language changes to update column headers
        this.languageProvider.currentLanguageSubject.subscribe(() => {
            this.initializeCateringColumns();
            this.selectableCateringProdListColumns.next(this.cateringProdListColumns.value.filter((col, index) => index !== 4));
            this.initializeProductlIstColumns();
            this.firstTime ? this.productListSelectableColumns.next(this.productListColumns.value.filter((col, index) => index !== 4)) : null
            this.firstTime = false;
        });
    }


    setCurrentPage(page: number) {
        this.currentPageSubject.next(page);
    }

    getCurrentPage() {
        return this.currentPageSubject.value;
    }

    setProductListColumns(columns: Column[]) {
        this.productListSelectableColumns.next(columns);
    }
    getProductListColumns() {
        return this.productListSelectableColumns.value;
    }
    initializeProductlIstColumns() {
        const columns =[
            { field: 'stockBalance', header: this.languagePipe.transform('Lagersaldo', 'STOCK_BALANCE', this.languageProvider.currentLanguage) },
            { field: 'bonusVoucherPercent', header: this.languagePipe.transform('Bonus', 'BONUS', this.languageProvider.currentLanguage) },
            { field: 'buyWithBonusDisabled', header: this.languagePipe.transform('Bonusköp', 'BONUS_BUY', this.languageProvider.currentLanguage) },
            { field: 'type.bongCategoryType.label', header: this.languagePipe.transform('BONG_CATEGORY', 'BONG_CATEGORY', this.languageProvider.currentLanguage) },
            { field: 'type.productCategoryType.label', header: this.languagePipe.transform('PRODUCT_CATEGORY', 'PRODUCT_CATEGORY', this.languageProvider.currentLanguage) },
            { field: 'tags', header: this.languagePipe.transform('Taggar', 'TAGS', this.languageProvider.currentLanguage) },
            { field: 'externalOrigin.externalId', header: this.languagePipe.transform('', 'EXTERNAL_ID', this.languageProvider.currentLanguage) }]
        this.productListColumns.next(columns);
    }

    setCateringProductListColumns(columns: Column[]) {
        this.selectableCateringProdListColumns.next(columns);
    }

    getCateringProdListColumns() {
        return this.selectableCateringProdListColumns.value;
    }

    initializeCateringColumns(): void {
        const lang = this.languageProvider.currentLanguage;
        this.cateringProdListColumns.next([
            { field: 'dietaryTags', header: this.languageProvider.getString('CATERING_DIETARY_TAGS', lang) || 'Dietary Tags' },
            { field: 'ecoTags', header: this.languageProvider.getString('CATERING_ECO_TAGS', lang) || 'Eco Tags' },
            { field: 'externalID', header: this.languageProvider.getString('EXTERNAL_ID', lang) || 'External ID'},
            { field: 'stockBalance', header: this.languageProvider.getString('STOCK_BALANCE', lang) || 'Stock Balance'},
            { field: 'category', header: this.languageProvider.getString('CATEGORY', lang) || 'Category'},
            { field: 'productType', header: this.languageProvider.getString('PRODUCT_TYPE', lang) || 'Product Type'},
            { field: 'servingsPerProduct', header: this.languageProvider.getString('SERVINGS', lang) || 'Servings'},
            { field: 'minimumOrderQuantity', header: this.languageProvider.getString('MIN_ORDER', lang) || 'Min Order'},
        ]);
        this.selectableCateringProdListColumns.next(this.cateringProdListColumns.value);
    }

    setCurrentTableRows(rows: number) {
        this.currentRowSubject.next(rows);
    }

    getCurrentTableRows() {
        return this.currentRowSubject.value;
    }

    // ------- Catering products helpers (admin UI) -------
    setCateringProducts(products: any[]) {
        this.cateringProducts$.next(Array.isArray(products) ? products : []);
    }

    upsertCateringProduct(product: any) {
        const list = [...(this.cateringProducts$.value || [])];
        const idx = list.findIndex(p => p?.id === product?.id);
        if (idx >= 0) list[idx] = product; else list.unshift(product);
        this.cateringProducts$.next(list);
    }

    removeCateringProductById(id: any) {
        const list = (this.cateringProducts$.value || []).filter(p => p?.id !== id);
        this.cateringProducts$.next(list);
    }

    getCateringProducts$(): Observable<any[]> {
        return this.cateringProducts$.asObservable();
    }

    getCateringProductsSnapshot(): any[] {
        return this.cateringProducts$.value || [];
    }

    generateProductKey() {
        return this.afDatabase.createPushId();
    }
    refsSet(): boolean {
        return typeof (this.productsRef) !== "undefined" ||
            typeof (this.customMenusRef) !== "undefined" ||
            typeof (this.activeMenuRef) !== "undefined";
    }
    getProductTypes(): Observable<ProductType[]> {
        let observable = Observable.create((observer) => {
            if (this.productTypes) {
                observer.next(this.productTypes);
                observer.complete();
            }
            else
                this.afDatabase.database.ref("productTypes").once("value").then(x => {
                    this.productTypes = x.val();
                    observer.next(this.productTypes);
                    observer.complete;
                });
        });
        return observable;
    }
    async getBongCategoryTypes() {
        if (!this.productInfoTypes) {
            this.productInfoTypes = (await this.afDatabase.database.ref("types").once("value")).val();
            return this.productInfoTypes.bongCategoryTypes;
        }
        else
            return this.productInfoTypes.bongCategoryTypes;
    }
    async getProductCategoryTypes() {
        if (!this.productInfoTypes) {
            this.productInfoTypes = (await this.afDatabase.database.ref("types").once("value")).val();
            return this.productInfoTypes.productCategoryTypes;
        }
        else
            return this.productInfoTypes.productCategoryTypes;
    }
    // subscribeToCustomMenu(key) {
    //     return this.afDatabase.object<IMenu>(
    //         `Clients/${this.currentClientUnitRoute}/customMenus/${key}`
    //     ).snapshotChanges()
    //         .pipe(
    //             map(c => ({ key: c.payload.key, ...c.payload.val() })
    //             )
    //         );
    // }

    subscribeToCustomMenu(key: string): Observable<IMenu> {
        return this.afDatabase.object<IMenu>(
            `Clients/${this.currentClientUnitRoute}/customMenus/${key}`
        )
            .snapshotChanges()
            .pipe(
                take(1),
                map(c => ({ key: c.payload.key, ...c.payload.val() }))
            );
    }

    ensureProductsForRoute(routeName: string): void {
        const route = routeName?.trim();
        if (!route) return;
        if ((this.productsOnlyRoute === route && this.productsOnlySub) ||
            (route === this.currentClientUnitRoute && this.productsRef)) return;

        this.productsOnlySub?.unsubscribe();
        this.productsOnlySub = null;
        this.productsOnlyRoute = route;
        this.productsOnlySub = this.afDatabase
            .list<IMenuItem>(`Clients/${route}/menuItems`)
            .snapshotChanges()
            .pipe(map(changes => changes.map(c => ({ ...c.payload.val(), key: c.payload.key }))))
            .subscribe(x => this.products.next(x));
    }

    async setupDbRefsForRoute(routeName: string) {
        if (routeName === this.currentClientUnitRoute && this.refsSet()) {
            return;
        }
        if (this.productsOnlySub) {
            this.productsOnlySub.unsubscribe();
            this.productsOnlySub = null;
            this.productsOnlyRoute = null;
        }
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
        this.currentClientUnitRoute = routeName;
        this.settingsService.setType(null);
        this.settingsService.setproductType(null);
        this.settingsService.setbongCategoryType(null);
        this.settingsService.setSearchedData(null);
        this.setCurrentPage(0);
        this.setCurrentTableRows(25);
        this.productFetchCache.clear();
        this.globalSubCategoriesRef = this.afDatabase.list<any>(
            `Clients/${routeName}/generalCategories`
        );

        this.subscriptions.add(
            this.globalSubCategoriesRef
            .snapshotChanges()
            .pipe(
                map((changes: any[]) => changes.map(c => (
                    { key: c.payload.key, ...c.payload.val() }
                ))
                )).subscribe(x => {
                    this.subCategoriesGroup.next(x);
                })
        )
        this.productsRef = this.afDatabase.list<IMenuItem>(
            `Clients/${routeName}/menuItems`
        );
        let prodSub = this.productsRef
            .snapshotChanges()
            .pipe(
                map(changes => changes.map(c => (
                    { ...c.payload.val(), key: c.payload.key }
                ))
                )).subscribe(x => {
                    this.products.next(x);
                });
        this.subscriptions.add(prodSub);

        const weeklyMenuProductsList = this.afDatabase.list<IMenuItem>(
            `Clients/${routeName}/menuItems`,
            (ref) => ref.orderByChild('isWeeklyMenuProduct').equalTo(true)
        );
        const weeklyProdSub = weeklyMenuProductsList
            .snapshotChanges()
            .pipe(
                map((changes) =>
                    changes.map((c) => ({ key: c.payload.key, ...c.payload.val() }))
                )
            )
            .subscribe((x) => this.weeklyMenuProducts.next(x));
        this.subscriptions.add(weeklyProdSub);

        this.customMenusRef = this.afDatabase.list<IMenu>(
            `Clients/${routeName}/customMenus`
        );
        let cmSub = this.customMenusRef
            .snapshotChanges()
            .pipe(
                map(changes =>
                    changes.map(c => ({ key: c.payload.key, ...c.payload.val() }))
                )
            ).subscribe({
                next: x => {
                    this.dbRefsSetup = true;
                    this.customMenus.next(x);
                },
                error: err => {
                    // console.error('Error fetching customMenus:', err);
                    this.customMenus.error(err);
                }
            });;
        this.subscriptions.add(cmSub);
        this.activeMenuRef = this.afDatabase.object<IMenu>(
            `Clients/${routeName}/activeMenu`
        );
        let amsub = this.activeMenuRef.valueChanges().subscribe(x => {
            this.activeMenu.next(x);
        });
        this.subscriptions.add(amsub);
        this.unitService.setToggleFlags(routeName);
    }

    getProduct(id: string, routeName: string): any {
        if (this.currentClientUnitRoute === undefined) {
            this.setupDbRefsForRoute(routeName)
            return this.afDatabase.object<IMenuItem>(`Clients/${routeName}/menuItems/${id}`)
                .snapshotChanges();
        }
        return this.afDatabase.object<IMenuItem>(`Clients/${this.currentClientUnitRoute}/menuItems/${id}`)
            .snapshotChanges();
    }
    async getProductQrcodeFlag(routeName: string) {

        let productFlag = (await this.afDatabase.database.ref(`adminApp/clients/${this.currentClientUnitRoute}/settings/flags/qrCodeForProducts`).once("value")).val();
        return productFlag;
    }
    async addProduct(item, key?): Promise<any> {
        if (key) {
            await this.afDatabase.database.ref(`Clients/${this.currentClientUnitRoute}/menuItems/${key}`).set(item);
            return key;
        }
        return this.productsRef.push(item);
    }
    updateProduct(item, options?: { key, routeName }) {
        if (options) {
            if (!options.key || !options.routeName) {
                alert("Key and Routename needs to be set as options. Please contact support");
                return;
            }
            const payload =
                item &&
                Array.isArray(item.addonsV2?.groups) &&
                item.addonsV2.groups.length > 0
                    ? this.sanitizeProductForAddonSave(item)
                    : item;
            this.afDatabase.database.ref(`Clients/${options.routeName}/menuItems/${options.key}`).set(payload);
        } else if (item) {
            const payload =
                Array.isArray(item.addonsV2?.groups) && item.addonsV2.groups.length > 0
                    ? this.sanitizeProductForAddonSave(item)
                    : item;
            return this.productsRef.update(item.key, payload);
        }
    }

    /** One-shot read for a single menu item (cached per route until `setupDbRefsForRoute` clears). */
    async getProductOnce(key: string): Promise<IMenuItem | null> {
        const route = this.currentClientUnitRoute;
        if (!key?.trim() || !route) {
            return null;
        }
        const cacheKey = `${route}:${key}`;
        if (this.productFetchCache.has(cacheKey)) {
            return this.productFetchCache.get(cacheKey) ?? null;
        }
        const snap = await this.afDatabase.database
            .ref(`Clients/${route}/menuItems/${key}`)
            .once('value');
        const val = snap.val();
        const product: IMenuItem | null = val ? ({ key, ...val } as IMenuItem) : null;
        this.productFetchCache.set(cacheKey, product);
        return product;
    }

    /**
     * Minimal field update from weekly menu inline editor (no category / option side effects).
     * `priceConfig` keys match product editor / channels: `-1` eat-in, `0` takeaway, `-2` drive delivery, `-3` fixed location.
     * Pass `null` for a key to remove that override from RTDB.
     */
    async updateWeeklyMenuProductFields(
        key: string,
        fields: Pick<IMenuItem, 'Name' | 'Description' | 'DescriptionLong' | 'Cost' | 'enabled'> & {
            priceConfig?: Partial<Record<'-1' | '0' | '-2' | '-3', number | null>>;
        }
    ): Promise<void> {
        const route = this.currentClientUnitRoute;
        if (!key?.trim() || !route) {
            return;
        }
        const ref = this.afDatabase.database.ref(`Clients/${route}/menuItems/${key}`);
        const payload: Record<string, unknown> = {
            Name: fields.Name,
            Description: fields.Description,
            DescriptionLong: fields.DescriptionLong,
            Cost: fields.Cost,
            basePrice: fields.Cost,
            dateEdited: new Date()
        };
        if (fields.enabled !== undefined) {
            payload['enabled'] = !!fields.enabled;
        }
        if (fields.priceConfig) {
            for (const [subKey, v] of Object.entries(fields.priceConfig)) {
                if (!['-1', '0', '-2', '-3'].includes(subKey)) {
                    continue;
                }
                payload[`priceConfig/${subKey}`] = v === null || v === undefined ? null : v;
            }
        }
        await ref.update(payload);
    }

    /** Clear cached one-shot read so the next `getProductOnce` reflects RTDB. */
    invalidateProductFetchCacheEntry(key: string): void {
        const route = this.currentClientUnitRoute;
        if (!key?.trim() || !route) {
            return;
        }
        this.productFetchCache.delete(`${route}:${key}`);
    }
    async updateProductArray(items: IMenuItem[], routeName) {
        let promises = [];
        for (let index = 0; index < items.length; index++) {
            const element = items[index];
            const payload =
                Array.isArray(element.addonsV2?.groups) && element.addonsV2.groups.length > 0
                    ? this.sanitizeProductForAddonSave(element)
                    : element;
            promises.push(this.afDatabase.database.ref(`Clients/${routeName}/menuItems/${element.key}`).set(payload));
        }
        await Promise.all(promises);
        return true;
    }


    async updateActiveMenuItemEuDb(routeName, item, itemIndex, categoryIndex) {
        let body = {
            routeName: routeName,
            item: item,
            itemIndex: itemIndex,
            categoryIndex: categoryIndex
        }
        let url = `${environment.pubQueueApiUrl}/updateActiveMenuItemForRoute`;
        return await this.http.post<any[]>(url, body).toPromise();
    }

    async activateEuMenu(routeName, menu, options) {
        let body = {
            routeName: routeName,
            customMenu: menu,
            options: options
        }

        let url = `${environment.pubQueueApiUrl}/setActiveMenuForRoute`;
        return await this.http.post<any[]>(url, body).toPromise();
    }


    private sanitizeMenuForFirebase(menu: IMenu): void {
        if (!menu) {
            return;
        }
        if (!Array.isArray(menu.categories)) {
            menu.categories = [];
        }
        menu.categories.forEach(category => {
            if (category?.description === undefined) {
                category.description = '';
            }
            if (Array.isArray(category?.items)) {
                category.items.forEach(item => {
                    if (item?.tags === undefined) {
                        delete item.tags;
                    }
                    if (item?.ImageUrl === "img/meny/") {
                        item.ImageUrl =
                            "https://firebasestorage.googleapis.com/v0/b/pq-dev.appspot.com/o/menuPlaceholder.jpg?alt=media";
                    }
                });
            }
        });
    }

    setActiveMenu(routeName, customMenu: IMenu, options?: { key, routeName }) {
        this.sanitizeMenuForFirebase(customMenu);
        if (options) {
            if (!options.key || !options.routeName) {
                alert("Not enough options. please contact support");
                return;
            }
            // this.activateEuMenu(routeName, customMenu, options);
            this.afDatabase.database.ref(`Clients/${options.routeName}/activeMenu`).set(customMenu);
        }
        else {
            // this.activateEuMenu(routeName, customMenu, options);
            return this.activeMenuRef.set(customMenu);
        }
    }
    setActiveMenuWithPinCode(routeName, pinCode: string, customMenu: IMenu) {
        this.sanitizeMenuForFirebase(customMenu);
        return this.afDatabase.database.ref(`Clients/${routeName}/pinCodeMenus/${pinCode}`).set(customMenu);
    }
    updateActiveMenuItem(itemProps, itemIndex, categoryIndex: number | string) {
        this.updateActiveMenuItemEuDb(this.currentClientUnitRoute, itemProps, itemIndex, categoryIndex)
        return this.afDatabase.database.ref(`Clients/${this.currentClientUnitRoute}/activeMenu/categories/${categoryIndex}/items/${itemIndex}`).update(itemProps).then(x => {
            return x;
        }).catch(err => {
            throw err;
        });
    }

    saveCustomMenu(menu): Promise<any> {
        this.sanitizeMenuForFirebase(menu);
        return new Promise((resolve, reject) => {
            this.customMenusRef.set(menu.key, menu)
                .then(x => resolve(true))
                .catch(err => reject(err));
        });
    }

    async createCustomMenu(menu, options?: { key, routeName }): Promise<any> {
        if (options) {
            if (!options.key || !options.routeName) { alert("Not enough options. please contact support"); return; }
            return await this.afDatabase.database.ref(`Clients/${options.routeName}/customMenus/${options.key}`).set(menu);
        }
        else
            return await this.customMenusRef.push(menu)
    }
    async copyCustomMenu(menu) {
        let newMenu = JSON.parse(JSON.stringify(menu));
        newMenu.name = `${newMenu.name} Kopia ${moment().format("YYYY-MM-DD kk:mm")}`
        delete newMenu.key;
        const ref = this.customMenusRef.push(newMenu);
        newMenu.key = ref.key;
    }
    async deleteCustomMenu(menu, options?: { key, routeName }) {
        if (options) {
             this.afDatabase.database.ref(`Clients/${options.routeName}/customMenus/${options.key}`).set(null);
        }
        else
            this.customMenusRef.remove(menu.key);

        let result = await this.http
            .delete<any>(`${environment.pubQueueApiUrlEU}/v1/menu/tasks/${menu.key || options.key}`)
            .toPromise()
        return result;
    }
    deleteProduct(key) {
        this.productsRef.remove(key);
    }
    async deleteAllProducts() {
        return this.productsRef.remove();
    }
    getCategories() {
        return [
            {
                name: "Mat",
                id: "0"
            },
            {
                name: "Öl",
                id: "3"
            },
            {
                name: "Vin",
                id: "4"
            },
            {
                name: "Alkoholfri dryck",
                id: "1"
            },
            {
                name: "Cider",
                id: "2"
            },
            {
                name: "Alkoläsk",
                id: "6"
            },
            {
                name: "Sprit",
                id: "5"
            },
            {
                name: "Övrigt",
                id: "9"
            }
        ];
    }
    getCategoryTypes() {
        return ["Mat", "Dryck"];
    }
    loadImage = async src =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => resolve(img);
            img.onerror = (...args) => reject(args);
            img.src = src;
        });

    getImageData = image => {
        const canvas = document.createElement("canvas");

        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0);

        return context.getImageData(0, 0, image.width, image.height);
    };
    encodeImageToBlurhash = async imageUrl => {
        if (!imageUrl) {
            return;

        }
        const image = await this.loadImage(imageUrl);
        const imageData = this.getImageData(image);
        return encode(imageData.data, imageData.width, imageData.height, 4, 4);
    };
    async saveWeeklyMenuDescription(menuDescription, enabled: boolean) {
        let routeName = this.currentClientUnitRoute;
        return await this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/weeklyMenu`).update({ enabled, content: menuDescription });

    }

    async saveExternalWeeklyMenuDescription(url, enabled: boolean) {
        let routeName = this.currentClientUnitRoute;
        return await this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/externalWeeklyMenu`).update({ enabled, url });
    }
    async getWeeklyMenuDescription() {
        let routeName = this.currentClientUnitRoute;
        return (await this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/weeklyMenu`).once("value")).val();
    }

    getWeeklyMenusRef() {
        const routeName = this.currentClientUnitRoute;
        return this.afDatabase.object<IWeeklyMenus>(`Clients/${routeName}/AppInfo/Context/weeklyMenus`);
    }

    getDefaultWeeklyMenuDay(): IWeeklyMenuDay {
        return {
            closed: false,
            cutoffTime: "10:00",
            availableFrom: "11:00",
            availableUntil: "14:00",
            products: {}
        };
    }

    getDefaultWeeklyMenuServingOptions(): IWeeklyMenuServingOptions {
        return {
            // enabled: false,
            takeaway: { enabled: false },
            eatin: { enabled: false },
            deliveryToFixedLocations: { enabled: false, minimumFee: 0, locations: [] }
        };
    }

    getDefaultWeeklyMenuRollover(): IWeeklyMenuRolloverSettings {
        return {
            enabled: false,
            cutoffDayIndex: 4,
            cutoffTime: "18:00"
        };
    }

    private clientDisplayTimeZone = 'Europe/Stockholm';

    async resolveClientDisplayTimeZone(routeName: string): Promise<string> {
        if (!routeName) {
            return this.clientDisplayTimeZone;
        }

        const url = `${environment.pubQueueApiUrl}/settings/v1/getResolvedClientTimeZone`;
        try {
            const tz = await firstValueFrom(
                this.http.post(url, { routeName }, { responseType: 'text' })
            );
            const value = (tz || '').trim();
            this.clientDisplayTimeZone = value || this.getRouteNameTimeZoneFallback(routeName);
        } catch {
            this.clientDisplayTimeZone = this.getRouteNameTimeZoneFallback(routeName);
        }

        return this.clientDisplayTimeZone;
    }

    private getRouteNameTimeZoneFallback(routeName: string): string {
        const r = (routeName || '').toLowerCase();
        if (r.startsWith('compassfi')) {
            return 'Europe/Helsinki';
        }
        if (r.startsWith('compassdk')) {
            return 'Europe/Copenhagen';
        }
        if (r.startsWith('compassno')) {
            return 'Europe/Oslo';
        }
        return 'Europe/Stockholm';
    }

    getClientDisplayTimeZone(): string {
        return this.clientDisplayTimeZone;
    }

    getWeeklyMenuWeekInfo(date: Date | moment.Moment = new Date(), timezone = this.clientDisplayTimeZone): IWeeklyMenuWeekInfo {
        const start = moment.tz(date, timezone).startOf('isoWeek');
        const end = start.clone().endOf('isoWeek');
        return {
            weekKey: start.format('GGGG-[W]WW'),
            weekStartDate: start.format('YYYY-MM-DD'),
            weekEndDate: end.format('YYYY-MM-DD')
        };
    }

    getCurrentAndNextWeeklyMenuWeekInfo(now: Date | moment.Moment = new Date()): { current: IWeeklyMenuWeekInfo; next: IWeeklyMenuWeekInfo } {
        const current = this.getWeeklyMenuWeekInfo(now);
        const next = this.getWeeklyMenuWeekInfo(moment.tz(now, this.clientDisplayTimeZone).add(1, 'week'));
        return { current, next };
    }

    getWeeklyMenuWeekInfoByKey(weekKey: string, timezone = this.clientDisplayTimeZone): IWeeklyMenuWeekInfo {
        const match = String(weekKey || '').match(/^(\d{4})-W(\d{2})$/);
        if (!match) {
            return this.getWeeklyMenuWeekInfo(new Date(), timezone);
        }

        const isoWeekYear = Number(match[1]);
        const isoWeek = Number(match[2]);
        const start = moment.tz(timezone)
            .isoWeekYear(isoWeekYear)
            .isoWeek(isoWeek)
            .startOf('isoWeek');
        const end = start.clone().endOf('isoWeek');

        return {
            weekKey: start.format('GGGG-[W]WW'),
            weekStartDate: start.format('YYYY-MM-DD'),
            weekEndDate: end.format('YYYY-MM-DD')
        };
    }

    private getWeeklyMenuWeekIdentityPayload(weekKey: string, weekInfo?: IWeeklyMenuWeekInfo): Pick<IWeeklyMenuSlot, 'weekKey' | 'weekStartDate' | 'weekEndDate'> {
        const resolved = weekInfo || this.getWeeklyMenuWeekInfoByKey(weekKey);
        return {
            weekKey: resolved.weekKey,
            weekStartDate: resolved.weekStartDate,
            weekEndDate: resolved.weekEndDate
        };
    }

    getDefaultWeeklyMenuSlot(weekInfo?: IWeeklyMenuWeekInfo): IWeeklyMenuSlot {
        const weekdays: Record<string, IWeeklyMenuDay> = {};
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
            weekdays[dayIndex] = {
                ...this.getDefaultWeeklyMenuDay(),
                closed: dayIndex === 5 || dayIndex === 6
            };
        }

        return {
            ...(weekInfo ? {
                weekKey: weekInfo.weekKey,
                weekStartDate: weekInfo.weekStartDate,
                weekEndDate: weekInfo.weekEndDate
            } : {}),
            globalSettings: { orderDaysInAdvance: 3 },
            servingOptions: this.getDefaultWeeklyMenuServingOptions(),
            weekdays
        };
    }

    getDefaultWeeklyMenus(): IWeeklyMenus {
        return {
            settings: {
                rollover: this.getDefaultWeeklyMenuRollover(),
                timezone: this.clientDisplayTimeZone,
                servingOptions: this.getDefaultWeeklyMenuServingOptions()
            },
            weeks: {}
        };
    }

    private normalizeWeeklyMenuWeekdays(rawWeekdays: any): Record<string, IWeeklyMenuDay> {
        const weekdays: Record<string, IWeeklyMenuDay> = {};

        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
            const key = String(dayIndex);
            const day = rawWeekdays?.[key];
            const normalizedProducts = this.normalizeWeeklyMenuDayProducts(day?.products || {});

            weekdays[key] = {
                ...this.getDefaultWeeklyMenuDay(),
                ...day,
                products: normalizedProducts
            };
        }

        return weekdays;
    }

    /** Normalized serving-options shape for reads and writes (settings-level or legacy week-level). */
    normalizeWeeklyMenuServingOptions(raw: any): IWeeklyMenuServingOptions {
        const defaults = this.getDefaultWeeklyMenuServingOptions();
        return {
            ...defaults,
            ...(raw || {}),
            takeaway: {
                ...defaults.takeaway,
                ...(raw?.takeaway || {})
            },
            eatin: {
                ...defaults.eatin,
                ...(raw?.eatin || {})
            },
            deliveryToFixedLocations: {
                ...defaults.deliveryToFixedLocations,
                ...(raw?.deliveryToFixedLocations || {}),
                minimumFee: Number(raw?.deliveryToFixedLocations?.minimumFee ?? defaults.deliveryToFixedLocations.minimumFee ?? 0),
                locations: Array.isArray(raw?.deliveryToFixedLocations?.locations)
                    ? raw.deliveryToFixedLocations.locations
                        .filter((location: any) => !!location?.name)
                        .map((location: any) => {
                            let coupon: any = null;
                            if (location?.coupon && typeof location.coupon === 'object') {
                                coupon = JSON.parse(JSON.stringify(location.coupon));
                            } else if (location?.couponKey || location?.couponCode) {
                                coupon = {
                                    key: String(location.couponKey || ''),
                                    code: String(location.couponCode || '')
                                };
                            }

                            return {
                                name: String(location.name),
                                ...(coupon ? { coupon } : {})
                            };
                        })
                    : []
            }
        };
    }

    private normalizeWeeklyMenuSlot(raw: any, weekInfo?: IWeeklyMenuWeekInfo): IWeeklyMenuSlot {
        const defaultSlot = this.getDefaultWeeklyMenuSlot(weekInfo);
        return {
            ...(weekInfo?.weekKey || raw?.weekKey ? { weekKey: String(weekInfo?.weekKey || raw.weekKey) } : {}),
            ...(weekInfo?.weekStartDate || raw?.weekStartDate ? { weekStartDate: String(weekInfo?.weekStartDate || raw.weekStartDate) } : {}),
            ...(weekInfo?.weekEndDate || raw?.weekEndDate ? { weekEndDate: String(weekInfo?.weekEndDate || raw.weekEndDate) } : {}),
            globalSettings: {
                ...defaultSlot.globalSettings,
                ...(raw?.globalSettings || {})
            },
            servingOptions: this.normalizeWeeklyMenuServingOptions(raw?.servingOptions),
            weekdays: this.normalizeWeeklyMenuWeekdays(raw?.weekdays)
        };
    }

    private isLegacyWeeklyMenusShape(value: any): boolean {
        if (!value || typeof value !== 'object') {
            return false;
        }
        return (
            (value.weekdays || value.servingOptions || value.globalSettings) &&
            !value.current &&
            !value.next &&
            !value.weeks
        );
    }

    private isCurrentNextWeeklyMenusShape(value: any): boolean {
        if (!value || typeof value !== 'object') {
            return false;
        }
        return !!(value.current || value.next);
    }

    normalizeWeeklyMenus(value: any): IWeeklyMenus {
        const defaults = this.getDefaultWeeklyMenus();
        const { current, next } = this.getCurrentAndNextWeeklyMenuWeekInfo();
        const settingsBase: IWeeklyMenuSettings = {
            ...defaults.settings,
            ...(value?.settings || {}),
            rollover: {
                ...defaults.settings.rollover,
                ...(value?.settings?.rollover || value?.rollover || {})
            },
            timezone: this.clientDisplayTimeZone
        };
        const weeks: Record<string, IWeeklyMenuSlot> = {};

        if (this.isLegacyWeeklyMenusShape(value)) {
            weeks[current.weekKey] = this.normalizeWeeklyMenuSlot({
                globalSettings: value?.globalSettings,
                servingOptions: value?.servingOptions,
                weekdays: value?.weekdays
            }, current);
            const inheritedServing =
                value?.settings?.servingOptions != null
                    ? value.settings.servingOptions
                    : weeks[current.weekKey]?.servingOptions;
            settingsBase.servingOptions = this.normalizeWeeklyMenuServingOptions(
                inheritedServing ?? defaults.settings.servingOptions
            );
            return { settings: settingsBase, weeks };
        }

        if (this.isCurrentNextWeeklyMenusShape(value)) {
            if (value?.current) {
                weeks[current.weekKey] = this.normalizeWeeklyMenuSlot(value.current, current);
            }
            if (value?.next) {
                weeks[next.weekKey] = this.normalizeWeeklyMenuSlot(value.next, next);
            }
            const inheritedServing =
                value?.settings?.servingOptions != null
                    ? value.settings.servingOptions
                    : weeks[current.weekKey]?.servingOptions;
            settingsBase.servingOptions = this.normalizeWeeklyMenuServingOptions(
                inheritedServing ?? defaults.settings.servingOptions
            );
            return { settings: settingsBase, weeks };
        }

        Object.keys(value?.weeks || {}).forEach((weekKey) => {
            if (!weekKey) {
                return;
            }
            weeks[weekKey] = this.normalizeWeeklyMenuSlot({
                ...value.weeks[weekKey],
                weekKey
            });
        });

        const hasIndexedSettingsObject = !!(value?.settings && typeof value.settings === 'object');
        const inheritedServing =
            hasIndexedSettingsObject
                ? value.settings?.servingOptions
                : weeks[current.weekKey]?.servingOptions;
        settingsBase.servingOptions = this.normalizeWeeklyMenuServingOptions(
            inheritedServing ?? defaults.settings.servingOptions
        );

        return {
            settings: settingsBase,
            weeks
        };
    }

    getWeeklyMenus$(): Observable<IWeeklyMenus> {
        return this.getWeeklyMenusRef()
            .snapshotChanges()
            .pipe(
                map((snapshot: any) =>
                    this.normalizeWeeklyMenus(snapshot.payload.val())
                )
            );
    }

    async ensureWeeklyMenusInitialized(): Promise<IWeeklyMenus> {
        const routeName = this.currentClientUnitRoute;
        await this.resolveClientDisplayTimeZone(routeName);
        const ref = this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus`);
        const snapshot = await ref.once("value");
        const rawValue = snapshot.val();
        const normalized = this.normalizeWeeklyMenus(rawValue);

        if (!snapshot.exists()) {
            await ref.set({ settings: normalized.settings, weeks: normalized.weeks });
            return normalized;
        }

        if (this.isLegacyWeeklyMenusShape(rawValue) || this.isCurrentNextWeeklyMenusShape(rawValue)) {
            await ref.set({ settings: normalized.settings, weeks: normalized.weeks });
            return normalized;
        }

        // For indexed shape, avoid startup writes that can unintentionally overwrite
        // recently saved settings while listeners are still converging.
        return normalized;
    }

    async saveWeeklyMenus(weeklyMenus: IWeeklyMenus): Promise<void> {
        const routeName = this.currentClientUnitRoute;
        const normalized = this.normalizeWeeklyMenus(weeklyMenus);
        await this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus`).set({
            settings: normalized.settings,
            weeks: normalized.weeks
        });
    }

    async updateWeeklyMenuWeekGlobalSettings(weekKey: string, globalSettings: IWeeklyMenuGlobalSettings): Promise<void> {
        const routeName = this.currentClientUnitRoute;
        const identity = this.getWeeklyMenuWeekIdentityPayload(weekKey);
        await this.afDatabase.database
            .ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus/weeks/${weekKey}`)
            .update({
                ...identity,
                globalSettings
            });
    }

    async updateWeeklyMenuWeekDay(weekKey: string, dayIndex: number, day: IWeeklyMenuDay): Promise<void> {
        const routeName = this.currentClientUnitRoute;
        const identity = this.getWeeklyMenuWeekIdentityPayload(weekKey);
        const normalizedProducts = this.normalizeWeeklyMenuDayProducts(day?.products || {});

        const normalizedDay = {
            ...this.getDefaultWeeklyMenuDay(),
            ...day,
            products: normalizedProducts
        };
        await this.afDatabase.database
            .ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus/weeks/${weekKey}`)
            .update(identity);
        await this.afDatabase.database
            .ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus/weeks/${weekKey}/weekdays/${dayIndex}`)
            .set(normalizedDay);
    }

    private normalizeWeeklyMenuDayProducts(products: any): Record<string, IWeeklyMenuProductEntry> {
        const deduped: Record<string, IWeeklyMenuProductEntry> = {};
        const seen = new Set<string>();
        const ordered = Object.keys(products || {})
            .map((productIndexKey) => ({ productIndexKey, productEntry: products[productIndexKey] || {} }))
            .sort((a, b) => {
                const aOrder = Number(a.productEntry?.sortOrder ?? Number(a.productIndexKey) ?? 0);
                const bOrder = Number(b.productEntry?.sortOrder ?? Number(b.productIndexKey) ?? 0);
                return aOrder - bOrder;
            });

        ordered.forEach(({ productEntry }) => {
            const productKey = productEntry.productKey || productEntry.product?.key || "";
            if (!productKey || seen.has(productKey)) {
                return;
            }
            const index = Object.keys(deduped).length;
            seen.add(productKey);
            deduped[String(index)] = {
                sortOrder: index,
                productKey,
                available: productEntry.available === undefined ? true : !!productEntry.available
            };
        });

        return deduped;
    }

    async updateWeeklyMenuWeekServingOptions(weekKey: string, servingOptions: IWeeklyMenuServingOptions): Promise<void> {
        const routeName = this.currentClientUnitRoute;
        const identity = this.getWeeklyMenuWeekIdentityPayload(weekKey);
        const normalizedServingOptions = this.normalizeWeeklyMenuServingOptions(servingOptions);

        await this.afDatabase.database
            .ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus/weeks/${weekKey}`)
            .update({
                ...identity,
                servingOptions: normalizedServingOptions
            });
    }

    async updateWeeklyMenuRollover(rollover: IWeeklyMenuRolloverSettings): Promise<void> {
        const routeName = this.currentClientUnitRoute;
        const defaults = this.getDefaultWeeklyMenuRollover();
        const payload: IWeeklyMenuRolloverSettings = {
            enabled: !!(rollover?.enabled),
            cutoffDayIndex: Number(
                rollover?.cutoffDayIndex === undefined || rollover?.cutoffDayIndex === null
                    ? defaults.cutoffDayIndex
                    : rollover.cutoffDayIndex
            ),
            cutoffTime: String(rollover?.cutoffTime || defaults.cutoffTime)
        };
        await this.afDatabase.database
            .ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus/settings/rollover`)
            .set(payload);
    }

    async updateWeeklyMenuSettingsServingOptions(servingOptions: IWeeklyMenuServingOptions): Promise<void> {
        const routeName = this.currentClientUnitRoute;
        const normalizedServingOptions = this.normalizeWeeklyMenuServingOptions(servingOptions);
        await this.afDatabase.database
            .ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus/settings/servingOptions`)
            .set(normalizedServingOptions);
    }

    getIsoWeekStartDate(now: Date | moment.Moment = new Date()): string {
        return this.getWeeklyMenuWeekInfo(now).weekStartDate;
    }

    async resetWeeklyMenuWeek(weekKey: string, weekInfo?: IWeeklyMenuWeekInfo): Promise<IWeeklyMenuSlot> {
        const routeName = this.currentClientUnitRoute;
        const blank = this.getDefaultWeeklyMenuSlot(weekInfo || { ...this.getWeeklyMenuWeekInfo(), weekKey });
        blank.weekKey = weekKey;
        delete (blank as Partial<IWeeklyMenuSlot>).servingOptions;
        await this.afDatabase.database
            .ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus/weeks/${weekKey}`)
            .set(blank);
        return blank;
    }

    async cloneWeeklyMenuWeek(sourceWeekKey: string, targetWeekKey: string, targetWeekInfo?: IWeeklyMenuWeekInfo): Promise<IWeeklyMenuSlot> {
        const routeName = this.currentClientUnitRoute;
        const ref = this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus`);
        const snapshot = await ref.once('value');
        const menus = this.normalizeWeeklyMenus(snapshot.val());

        const source = menus.weeks?.[sourceWeekKey] || this.getDefaultWeeklyMenuSlot();
        const clone = this.normalizeWeeklyMenuSlot(JSON.parse(JSON.stringify(source)), targetWeekInfo);
        clone.weekKey = targetWeekKey;
        delete (clone as Partial<IWeeklyMenuSlot>).servingOptions;

        await ref.child(`weeks/${targetWeekKey}`).set(clone);
        return clone;
    }

    getOrphanWeeklyMenuWeekKeys(weeklyMenus?: IWeeklyMenus): string[] {
        const menus = weeklyMenus ?? this.getDefaultWeeklyMenus();
        const { current, next } = this.getCurrentAndNextWeeklyMenuWeekInfo();
        return Object.keys(menus.weeks || {})
            .filter((weekKey) => !!weekKey && weekKey !== current.weekKey && weekKey !== next.weekKey)
            .sort((a, b) => b.localeCompare(a));
    }

    async deleteOrphanWeeklyMenuWeeks(): Promise<string[]> {
        const routeName = this.currentClientUnitRoute;
        const ref = this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/weeklyMenus`);
        const snapshot = await ref.once('value');
        const menus = this.normalizeWeeklyMenus(snapshot.val());
        const orphanKeys = this.getOrphanWeeklyMenuWeekKeys(menus);

        for (const weekKey of orphanKeys) {
            await ref.child(`weeks/${weekKey}`).remove();
        }

        return orphanKeys;
    }

    async updateWeeklyMenuSlotGlobalSettings(slot: WeeklyMenuSlotId, globalSettings: IWeeklyMenuGlobalSettings): Promise<void> {
        const weekInfo = this.getCurrentAndNextWeeklyMenuWeekInfo()[slot];
        await this.updateWeeklyMenuWeekGlobalSettings(weekInfo.weekKey, globalSettings);
    }

    async updateWeeklyMenuSlotDay(slot: WeeklyMenuSlotId, dayIndex: number, day: IWeeklyMenuDay): Promise<void> {
        const weekInfo = this.getCurrentAndNextWeeklyMenuWeekInfo()[slot];
        await this.updateWeeklyMenuWeekDay(weekInfo.weekKey, dayIndex, day);
    }

    async updateWeeklyMenuSlotServingOptions(slot: WeeklyMenuSlotId, servingOptions: IWeeklyMenuServingOptions): Promise<void> {
        const weekInfo = this.getCurrentAndNextWeeklyMenuWeekInfo()[slot];
        await this.updateWeeklyMenuWeekServingOptions(weekInfo.weekKey, servingOptions);
    }

    async updateWeeklyMenuGlobalSettings(globalSettings: IWeeklyMenuGlobalSettings): Promise<void> {
        await this.updateWeeklyMenuSlotGlobalSettings('current', globalSettings);
    }

    async updateWeeklyMenuDay(dayIndex: number, day: IWeeklyMenuDay): Promise<void> {
        await this.updateWeeklyMenuSlotDay('current', dayIndex, day);
    }

    async updateWeeklyMenuServingOptions(servingOptions: IWeeklyMenuServingOptions): Promise<void> {
        await this.updateWeeklyMenuSlotServingOptions('current', servingOptions);
    }

    getCouponsForFixedLocations$(): Observable<IWeeklyMenuCouponOption[]> {
        const routeName = this.currentClientUnitRoute;
        return this.afDatabase
            .list<any>(`Clients/${routeName}/coupons`)
            .snapshotChanges()
            .pipe(
                map((changes: any) =>
                    changes
                        .map((change: any) => ({
                            key: change.payload.key,
                            ...change.payload.val()
                        }))
                        .filter((coupon: any) => !!coupon?.key)
                        .map((coupon: any) => ({
                            key: String(coupon.key),
                            code: String(
                                coupon.code ??
                                coupon.Code ??
                                coupon.couponCode ??
                                coupon.name ??
                                coupon.Name ??
                                coupon.key
                            )
                        }))
                        .filter((coupon: IWeeklyMenuCouponOption) => !!coupon.code.trim())
                )
            );
    }

    async getExternalWeeklyMenuDescription() {
        let routeName = this.currentClientUnitRoute;
        return (await this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/externalWeeklyMenu`).once("value")).val();
    }
    getGrinFlag() {
        let routeName = this.currentClientUnitRoute;
        return this.afDatabase
            .object<boolean>(`Clients/${routeName}/AppInfo/Context/grin`)
            .valueChanges();
    }
    public generateUniqueId(length) {
        var letters = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "x", "y", "z"]
        var capitalLetters = letters.map(x => x.toUpperCase());
        var numbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
        var approvedIdChars = letters.concat(capitalLetters).concat(numbers);
        var char = "";
        for (let i = 0; i < length; i += 1) {
            char += approvedIdChars[this.getRandomInt(1, approvedIdChars.length) - 1];
        }
        return char;
    }
    private getRandomInt(min, max) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }
    clearPreviousRouteMenu() {
        this.customMenus.next([]);
        this.dbRefsSetup = false;
    }

    /** Keep the current page path when switching units (with a few known remaps). */
    resolveRouteAppend(append: string): string {
        const routeMappings: Record<string, string> = {
            '/products/pro': '/products',
            '/menus/m/menus': '/menus/m/menus'
        };

        for (const key of Object.keys(routeMappings)) {
            if (append.includes(key)) {
                if (key === '/menus/m/menus') {
                    this.clearPreviousRouteMenu();
                }
                return routeMappings[key];
            }
        }
        return append;
    }

    clearPreviousRouteProducts() {
        this.products.next([]);
        this.weeklyMenuProducts.next([]);
    }

    async saveSubCategoryGroup(category, routeName) {
        // let url = `${environment.pubQueueApiUrl}/`;
        let data = {
            category: category,
            routeName
        }
        let url = `${environment.pubQueueApiUrlEU}/v1/menu/subcategoriesgroup/`;
        return await this.http.post<any[]>(url, data).toPromise();
    }

    async deleteGlobalSubCategory(id, routeName) {
        // let filtered = this.subCategoriesGroup.value.filter(x => x.id != id);
        // this.subCategoriesGroup.next([]);
        let url = `${environment.pubQueueApiUrlEU}/v1/menu/subcategoriesgroup/`;
        return await this.http.delete<any[]>(url + '/' + routeName + '/' + id).toPromise();
    }

    // async getSubCategoryGroup(routeName) {
    //     let url = `${environment.pubQueueApiUrlEU}/v1/menu/subcategoriesgroup?routeName=${routeName}`;
    //     return await this.http.get<any>(url).toPromise();
    // }

    getSubCategoryGroup(routeName: string) {
        const url = `${environment.pubQueueApiUrlEU}/v1/menu/subcategoriesgroup?routeName=${routeName}`;
        return this.http.get<any[]>(url).pipe(
            tap(data => this.subCategoriesGroup.next(data)) // push new data into subject
        ).toPromise();
    }

    setSortOrderForMenus(draggedIndex, droppedIndex) {
        const path = `Clients/${this.currentClientUnitRoute}/customMenus`;

        return this.afDatabase
            .object(path)
            .snapshotChanges() // Ensure you capture metadata
            .pipe(take(1))
            .toPromise()
            .then(async (snapshot: any) => {
                let dataa: any = {};
                dataa = snapshot.payload.val(); // Get data from snapshot
                if (!dataa) throw new Error('No data found at the specified path.');

                let data = {}
                data = this.hasSortOrder(dataa) ? dataa : this.addSortOrder(dataa);
                const updatedData = await this.reorderWithSortOrderUpdate(data, draggedIndex, droppedIndex);

                // Only update if there's an actual change
                await this.afDatabase.object(path).set(updatedData);

                dataa = {};
                return data;
            })
            .catch(error => {
                console.error('Error sorting custom menus:', error);
                throw error;
            });
    }

    addSortOrder(obj) {
        const entries = Object.entries(obj);
        const updatedEntries = entries.map((entry: any, index) => {
            const [key, value] = entry;
            return [key, {
                ...value,
                sortOrder: index
            }];
        });
        return Object.fromEntries(updatedEntries);
    }

    async reorderWithSortOrderUpdate(data, dragIndex, dropIndex) {
        const newData = { ...data };

        for (const key in newData) {
            const currentOrder = newData[key].sortOrder;

            if (dragIndex < dropIndex) {
                // Moving an item down
                if (currentOrder === dragIndex) {
                    newData[key].sortOrder = dropIndex;
                } else if (currentOrder > dragIndex && currentOrder <= dropIndex) {
                    newData[key].sortOrder = currentOrder - 1;
                }
            } else {
                // Moving an item up
                if (currentOrder === dragIndex) {
                    newData[key].sortOrder = dropIndex;
                } else if (currentOrder >= dropIndex && currentOrder < dragIndex) {
                    newData[key].sortOrder = currentOrder + 1;
                }
            }
        }

        return newData;
    }

    hasSortOrder(obj) {
        return Object.values(obj).every((value: any) => 'sortOrder' in value);
    }

    /** Rename this to avoid confusion */
    watchSubCategoriesGroup(): Observable<any[]> {
        return this.subCategoriesGroup.asObservable();
    }

    /** Optional: clear cache */
    clearSubCategories() {
        this.subCategoriesGroup.next([]);
    }

    // Rehydrate a stored product reference against the latest products list (by key).
    resolveProductReference(product: any, products: any[]) {
        if (!product?.key) {
            return product ?? null;
        }

        const matchedProduct = (products || []).find((item: any) => item?.key === product.key);
        return matchedProduct || product;
    }

    // Rehydrate linkedProduct/productId for options + nested choices (UI stability after reload).
    syncOptionsWithProductReferences(options: any[], products: any[]) {
        if (!Array.isArray(options)) {
            return [];
        }

        return options.map((option: any) => {
            const syncedOption = { ...option };
            if (this.hasAddonProductLink(option)) {
                const ref = option?.linkedProduct ?? option?.productId;
                const resolved = this.resolveProductReference(ref, products);
                if (resolved) {
                    const oldName = option.linkedProduct?.Name;
                    const oldCost = option.linkedProduct?.Cost != null ? Number(option.linkedProduct.Cost) : null;
                    
                    if (!option.text || option.text.trim() === '' || (oldName && option.text === oldName)) {
                        syncedOption.text = resolved.Name;
                    }
                    
                    if (option.cost == null || option.cost === '' || (oldCost !== null && Number(option.cost) === oldCost)) {
                        syncedOption.cost = resolved.Cost != null ? Number(resolved.Cost) : resolved.Cost;
                    }

                    const oldPriceConfig = option.linkedProduct?.priceConfig;
                    if (this.arePriceConfigsEqual(option.priceConfig, oldPriceConfig)) {
                        syncedOption.priceConfig = resolved.priceConfig ? { ...resolved.priceConfig } : null;
                    }

                    const oldImgUrl = option.linkedProduct?.ImageUrl;
                    if (!option.imageUrl || option.imageUrl.trim() === '' || (oldImgUrl && option.imageUrl === oldImgUrl)) {
                        syncedOption.imageUrl = resolved.ImageUrl;
                    }
                }
                syncedOption.linkedProduct = resolved;
                syncedOption.productId = resolved;
            } else {
                syncedOption.linkedProduct = null;
                syncedOption.productId = null;
            }

            if (Array.isArray(option?.choices)) {
                syncedOption.choices = option.choices.map((choice: any) => {
                    const syncedChoice = { ...choice };
                    if (this.hasAddonProductLink(choice)) {
                        const ref = choice?.linkedProduct ?? choice?.productId;
                        const resolved = this.resolveProductReference(ref, products);
                        if (resolved) {
                            const oldName = choice.linkedProduct?.Name;
                            const oldCost = choice.linkedProduct?.Cost != null ? Number(choice.linkedProduct.Cost) : null;
                            
                            if (!choice.text || choice.text.trim() === '' || (oldName && choice.text === oldName)) {
                                syncedChoice.text = resolved.Name;
                            }
                            
                            if (choice.cost == null || choice.cost === '' || (oldCost !== null && Number(choice.cost) === oldCost)) {
                                syncedChoice.cost = resolved.Cost != null ? Number(resolved.Cost) : resolved.Cost;
                            }

                            const oldPriceConfig = choice.linkedProduct?.priceConfig;
                            if (this.arePriceConfigsEqual(choice.priceConfig, oldPriceConfig)) {
                                syncedChoice.priceConfig = resolved.priceConfig ? { ...resolved.priceConfig } : null;
                            }

                            const oldImgUrl = choice.linkedProduct?.ImageUrl;
                            if (!choice.imageUrl || choice.imageUrl.trim() === '' || (oldImgUrl && choice.imageUrl === oldImgUrl)) {
                                syncedChoice.imageUrl = resolved.ImageUrl;
                            }
                        }
                        syncedChoice.linkedProduct = resolved;
                        syncedChoice.productId = resolved;
                    } else {
                        syncedChoice.linkedProduct = null;
                        syncedChoice.productId = null;
                    }
                    return syncedChoice;
                });
            }

            return syncedOption;
        });
    }

    // Payload helper: prevent deep/cyclic writes by stripping nested product.options.
    sanitizeProductReference(product: any) {
        if (!product || typeof product !== 'object') {
            return product ?? null;
        }

        return {
            ...product,
            options: null,
        };
    }

    normalizeAddonPriceConfig(priceConfig: any): Record<string, number> {
        if (!priceConfig) {
            return {};
        }
        if (Array.isArray(priceConfig)) {
            const out: Record<string, number> = {};
            priceConfig.forEach((val, idx) => {
                if (val !== undefined && val !== null) {
                    const n = Number(val);
                    if (Number.isFinite(n)) {
                        out[String(idx)] = n;
                    }
                }
            });
            return out;
        }
        if (typeof priceConfig !== 'object') {
            return {};
        }
        const out: Record<string, number> = {};
        for (const [key, raw] of Object.entries(priceConfig)) {
            const n = Number(raw);
            if (Number.isFinite(n)) {
                out[String(key)] = n;
            }
        }
        return out;
    }

    arePriceConfigsEqual(pc1: any, pc2: any): boolean {
        const norm1 = this.normalizeAddonPriceConfig(pc1);
        const norm2 = this.normalizeAddonPriceConfig(pc2);
        const keys1 = Object.keys(norm1);
        const keys2 = Object.keys(norm2);
        if (keys1.length !== keys2.length) {
            return false;
        }
        return keys1.every(key => norm1[key] === norm2[key]);
    }

    private optionalAddonPriceConfig(obj: any): { priceConfig?: Record<string, number> } {
        const normalized = this.normalizeAddonPriceConfig(obj?.priceConfig);
        return Object.keys(normalized).length > 0 ? { priceConfig: normalized } : {};
    }

    hasAddonProductLink(row: any): boolean {
        const ref = row?.linkedProduct ?? row?.productId;
        if (ref == null) {
            return false;
        }
        if (typeof ref === 'string') {
            return ref.trim() !== '';
        }
        if (typeof ref === 'object' && !Array.isArray(ref)) {
            if (ref.key != null && String(ref.key).trim() !== '') {
                return true;
            }
            if (typeof ref.Name === 'string' && ref.Name.trim() !== '') {
                return true;
            }
            return Object.keys(ref).length > 0;
        }
        return false;
    }

    clearAddonProductLink(target: any): void {
        if (!target) {
            return;
        }
        target.linkedProduct = null;
        target.productId = null;
    }

    private addonProductRefsForSave(row: any): { linkedProduct: any; productId: any } {
        if (!this.hasAddonProductLink(row)) {
            return { linkedProduct: null, productId: null };
        }
        const ref = row?.linkedProduct ?? row?.productId;
        const sanitized = this.sanitizeProductReference(ref);
        return { linkedProduct: sanitized, productId: sanitized };
    }

    applyLinkedMenuProductToAddonRow(target: any, product: IMenuItem | null | undefined, forceOverwrite: boolean = false): void {
        if (!target || !product) {
            return;
        }
        const ref = this.sanitizeProductReference(product);
        target.linkedProduct = ref;
        target.productId = ref;
        if (typeof product.Name === 'string') {
            if (forceOverwrite || !target.text || target.text.trim() === '') {
                target.text = product.Name;
            }
        }
        if (Number.isFinite(Number(product.Cost))) {
            if (forceOverwrite || target.cost == null || target.cost === '') {
                target.cost = Number(product.Cost);
            }
        }
        target.enabled = product.enabled !== false;
        const priceConfig = this.normalizeAddonPriceConfig(product.priceConfig);
        if (Object.keys(priceConfig).length > 0) {
            if (forceOverwrite || !target.priceConfig || Object.keys(target.priceConfig).length === 0) {
                target.priceConfig = { ...priceConfig };
            }
        }
        const url = typeof product.ImageUrl === 'string' ? product.ImageUrl.trim() : '';
        if (url) {
            if (forceOverwrite || !target.imageUrl || target.imageUrl.trim() === '') {
                target.imageUrl = url;
            }
        }
    }

    // Payload helper: sanitize one option (and its choices) for Firebase-safe writes.
    sanitizeOptionForSave(option: any) {
        const optionalImageUrl = (obj: any) => {
            const url = typeof obj?.imageUrl === 'string' ? obj.imageUrl.trim() : '';
            return url ? { imageUrl: url } : {};
        };
        const normalizedChoices = Array.isArray(option?.choices)
            ? option.choices.map((choice: any) => {
                const { imageUrl: _omitImg, priceConfig: _omitPc, ...choiceRest } = choice ?? {};
                return {
                    ...choiceRest,
                    ...this.addonProductRefsForSave(choice),
                    ...optionalImageUrl(choice),
                    ...this.optionalAddonPriceConfig(choice),
                };
            })
            : null;

        const { imageUrl: _omitImg, priceConfig: _omitPc, ...optionRest } = option ?? {};
        return {
            ...optionRest,
            ...this.addonProductRefsForSave(option),
            choices: normalizedChoices,
            ...optionalImageUrl(option),
            ...this.optionalAddonPriceConfig(option),
        };
    }

    /**
     * Boolean addon groups: persist like multiple — one option row with `choices[]` (not N sibling booleans).
     */
    collapseBooleanOptionGroupRowsForPayload(group: any, options: any[]): any[] {
        if (!Array.isArray(options) || options.length === 0) {
            return options;
        }
        const v2Type = group?.addonsV2?.type;
        const legacyType = group?.type;
        const isBooleanGroup =
            v2Type === 'boolean' ||
            (v2Type == null && legacyType === 'boolean') ||
            (v2Type == null &&
                legacyType == null &&
                options.length > 0 &&
                options.every((o) => o?.type === 'boolean'));
        if (!isBooleanGroup || v2Type === 'multiple') {
            return options;
        }
        if (options.length === 1 && Array.isArray(options[0]?.choices) && options[0].choices.length > 0) {
            return options.map((o) => ({ ...o }));
        }
        if (options.length === 1) {
            return options.map((o) => ({ ...o }));
        }

        const head = options[0];
        const parentStep = head?.step;
        const choices = options.map((row: any) => {
            const choice: Record<string, unknown> = {
                id: row.id,
                text: row.text ?? '',
                cost: Number.isFinite(Number(row.cost)) ? Number(row.cost) : 0,
                enabled: row.enabled !== false,
            };
            if (this.hasAddonProductLink(row)) {
                const refs = this.addonProductRefsForSave(row);
                choice['linkedProduct'] = refs.linkedProduct;
                choice['productId'] = refs.productId;
            }
            if (row.step != null && row.step !== '') choice['step'] = row.step;
            const url = typeof row?.imageUrl === 'string' ? row.imageUrl.trim() : '';
            if (url) choice['imageUrl'] = url;
            const pc = this.normalizeAddonPriceConfig(row?.priceConfig);
            if (Object.keys(pc).length > 0) {
                choice['priceConfig'] = pc;
            }
            return choice;
        });

        const parent: Record<string, unknown> = {
            ...head,
            id: head?.id,
            type: 'boolean',
            text: String(group?.title ?? head?.title ?? head?.text ?? '').trim() || String(head?.text ?? ''),
            title: head?.title ?? group?.title ?? '',
            choices,
        };
        delete parent['selected'];
        if (parentStep != null && parentStep !== '') {
            parent['step'] = parentStep;
        }
        parent['type'] = 'boolean';
        return [parent];
    }

    /** Copy `addonsV2.options[].priceConfig` onto legacy editor rows (by option id). */
    hydrateLegacyOptionPricesFromAddonsV2(group: any): void {
        const v2Options = Array.isArray(group?.addonsV2?.options) ? group.addonsV2.options : [];
        if (!v2Options.length || !Array.isArray(group?.options)) {
            return;
        }
        const byId = new Map<string, any>();
        for (const row of v2Options) {
            if (row?.id != null && String(row.id) !== '') {
                byId.set(String(row.id), row);
            }
        }
        const apply = (legacyRow: any) => {
            if (!legacyRow?.id) {
                return;
            }
            const v2 = byId.get(String(legacyRow.id));
            const pc = this.normalizeAddonPriceConfig(v2?.priceConfig ?? legacyRow?.priceConfig);
            if (Object.keys(pc).length > 0) {
                legacyRow.priceConfig = pc;
            }
        };
        for (const option of group.options) {
            if (Array.isArray(option?.choices) && option.choices.length > 0) {
                option.choices.forEach(apply);
            } else {
                apply(option);
            }
        }
    }

    /** Inverse of {@link collapseBooleanOptionGroupRowsForPayload} for the option-group editor UI. */
    expandBooleanNestedOptionGroupForEditor(group: any): void {
        if (!group?.options?.length) {
            return;
        }
        const v2Type = group?.addonsV2?.type;
        const legacyType = group?.type;
        const resolved = v2Type || legacyType || group.options[0]?.type;
        if (resolved !== 'boolean') {
            return;
        }
        if (group.options.length !== 1) {
            return;
        }
        const only = group.options[0];
        if (!Array.isArray(only?.choices) || only.choices.length === 0) {
            return;
        }

        const snapServing = only.servingmethods;
        const snapVis = only.visibleForMenu;
        const snapStep = only.step;
        const v2ById = new Map<string, any>();
        for (const row of Array.isArray(group?.addonsV2?.options) ? group.addonsV2.options : []) {
            if (row?.id != null && String(row.id) !== '') {
                v2ById.set(String(row.id), row);
            }
        }
        group.options = only.choices.map((ch: any, idx: number) => {
            const v2 = ch?.id != null ? v2ById.get(String(ch.id)) : undefined;
            const imageUrl =
                (typeof ch?.imageUrl === 'string' ? ch.imageUrl.trim() : '') ||
                (typeof v2?.imageUrl === 'string' ? v2.imageUrl.trim() : '');
            const priceConfig = this.normalizeAddonPriceConfig(ch?.priceConfig ?? v2?.priceConfig);
            return {
                ...ch,
                ...(imageUrl ? { imageUrl } : {}),
                ...(Object.keys(priceConfig).length > 0 ? { priceConfig } : {}),
                type: 'boolean',
                title: idx === 0 ? only.title : ch.title,
                text: ch.text ?? '',
                cost: Number.isFinite(Number(ch.cost)) ? Number(ch.cost) : 0,
                enabled: ch.enabled !== false,
                servingmethods: ch.servingmethods ?? snapServing,
                visibleForMenu: ch.visibleForMenu ?? snapVis,
                step: ch.step ?? snapStep,
                choices: null,
                linkedProduct: ch.linkedProduct ?? v2?.linkedProduct ?? null,
                productId: ch.productId ?? v2?.productId ?? null,
            };
        });
    }

    /**
     * Rows to append to `product.options` when linking a template group.
     * - default: boolean groups are collapsed to one parent row with choices[]
     * - legacy mode: keep flat boolean sibling rows for backward compatibility
     */
    getOptionRowsForProductFromGroup(
        group: any,
        optionGroupId: any,
        servingmethods: string[],
        preserveLegacyBooleanRows: boolean = false,
    ): any[] {
        const raw = Array.isArray(group?.options) ? group.options : [];
        const rows = preserveLegacyBooleanRows
            ? this.expandBooleanRowsForLegacyProduct(group, raw)
            : this.collapseBooleanOptionGroupRowsForPayload(group, raw);
        return rows.map((row) => ({
            ...row,
            optionGroupId,
            visibleForMenu: group.visibleForMenu,
            servingmethods: Array.isArray(servingmethods) ? [...servingmethods] : [],
        }));
    }

    /**
     * Legacy product storage expects flat boolean rows in `product.options`.
     * If template options are nested (single parent with `choices[]`), expand them.
     */
    private expandBooleanRowsForLegacyProduct(group: any, options: any[]): any[] {
        if (!Array.isArray(options) || !options.length) return [];
        const first = options[0];
        const isBoolean =
            group?.addonsV2?.type === 'boolean' ||
            group?.type === 'boolean' ||
            first?.type === 'boolean';
        if (!isBoolean) {
            return options.map((o: any) => ({ ...o }));
        }
        if (options.length === 1 && Array.isArray(first?.choices) && first.choices.length > 0) {
            const v2ById = new Map<string, any>();
            for (const row of Array.isArray(group?.addonsV2?.options) ? group.addonsV2.options : []) {
                if (row?.id != null && String(row.id) !== '') {
                    v2ById.set(String(row.id), row);
                }
            }
            return first.choices.map((choice: any) => {
                const v2 = choice?.id != null ? v2ById.get(String(choice.id)) : undefined;
                const imageUrl =
                    (typeof choice?.imageUrl === 'string' ? choice.imageUrl.trim() : '') ||
                    (typeof v2?.imageUrl === 'string' ? v2.imageUrl.trim() : '');
                return {
                    ...choice,
                    ...(imageUrl ? { imageUrl } : {}),
                    type: 'boolean',
                    title: choice?.title ?? first?.title ?? group?.title ?? '',
                    text: choice?.text ?? '',
                    cost: Number.isFinite(Number(choice?.cost)) ? Number(choice.cost) : 0,
                    enabled: choice?.enabled !== false,
                    selected: false,
                    choices: null,
                };
            });
        }
        return options.map((o: any) => ({
            ...o,
            type: 'boolean',
            choices: null,
        }));
    }

    // Payload helper: sanitize an option group before saving to API.
    /** Only fields stored in ProductOptionGroups.JsonData — never spread UI-only props (e.g. `availableGroups`). */
    sanitizeOptionGroupForSave(group: any) {
        const addonsV2 = this.sanitizeAddonsV2GroupForSave(group?.addonsV2, group);
        const rawOptions = Array.isArray(group?.options) ? group.options : [];
        const collapsed = this.collapseBooleanOptionGroupRowsForPayload(group, rawOptions);
        const options = collapsed.map((option: any) => this.sanitizeOptionForSave(option));
        const type = group?.type ?? group?.addonsV2?.type ?? collapsed[0]?.type ?? 'boolean';
        const payload: Record<string, unknown> = {
            routeName: group?.routeName,
            title: group?.title ?? '',
            description: group?.description ?? '',
            visibleForMenu: group?.visibleForMenu ?? 'all',
            servingmethods: Array.isArray(group?.servingmethods) ? [...group.servingmethods] : [],
            type,
            options,
            addonsV2,
        };
        if (group?.id != null && group.id !== '') {
            payload['id'] = group.id;
        }
        if (Array.isArray(group?.associatedProducts)) {
            payload['associatedProducts'] = group.associatedProducts.map((item: AssociatedProduct) => ({
                id: String(item.id),
                name: String(item.name ?? ''),
                price: Number(item.price ?? 0),
                image: String(item.image ?? ''),
                enabled: item.enabled !== false,
                description: String(item.description ?? ''),
            }));
        }
        return payload;
    }

    // Payload helper: sanitize a product before saving addon-linked options to Firebase.
    sanitizeProductForAddonSave(product: any) {
        const sanitizedAddonsV2 = this.sanitizeProductAddonsV2ForSave(product?.addonsV2, product);
        const addonGroupIds = new Set(
            (sanitizedAddonsV2?.groups || []).map((g: AddonsV2Group) => String(g.groupId)),
        );
        const rawOptions = Array.isArray(product?.options) ? product.options : [];
        const retainedOptions = rawOptions.filter((option: any) => {
            if (option?.optionGroupId == null) return true; // keep unique/manual options
            return !addonGroupIds.has(String(option.optionGroupId));
        });
        const sanitizedOptions = retainedOptions.map((option: any) => this.sanitizeOptionForSave(option));


        return {
            ...product,
            // V2-only persistence for linked addon groups: keep manual/unique options, drop rows covered by addonsV2.
            options: sanitizedOptions.length > 0 ? sanitizedOptions : null,
            addonsV2: sanitizedAddonsV2,
        };
    }

    /**
     * Infer SQL option-group shape from flat product.options rows so we can rebuild stable groupId / choice ids.
     */
    private inferLegacyOptionGroupShellFromProduct(product: any, g: any): any | undefined {
        if (!product?.options?.length) {
            return undefined;
        }
        const title = (g?.title || '').trim();
        let rows = product.options.filter(
            (o: any) => o?.optionGroupId != null && String(o.optionGroupId) === String(g?.groupId),
        );
        if (!rows.length || this.isPlaceholderAddonsV2GroupId(String(g?.groupId ?? ''))) {
            rows = product.options.filter((o: any) => {
                const ot = (o?.title || '').trim();
                return ot === title || (title.length > 0 && ot.includes(title));
            });
        }
        if (!rows.length) {
            return undefined;
        }
        const sorted = [...rows].sort((a, b) => String(a.text || '').localeCompare(String(b.text || '')));
        const first = sorted[0];
        if (first?.type === 'multiple' && Array.isArray(first.choices)) {
            return {
                id: first.optionGroupId,
                title: g.title ?? first.title,
                type: 'multiple',
                options: [{ ...first }],
            };
        }
        return {
            id: first.optionGroupId,
            title: g.title ?? first.title,
            type: 'boolean',
            options: sorted.map((r: any) => ({ ...r })),
        };
    }

    /** Ids like `group-new-group-0` must not override real ProductOptionGroups.Id (e.g. 168). */
    private isPlaceholderAddonsV2GroupId(id: string): boolean {
        return /^group-new-group-/i.test(id) || id === 'new-group';
    }

    private resolveAddonsV2GroupId(block: any, parentOptionGroup?: any): string {
        const pid = parentOptionGroup?.id ?? parentOptionGroup?.groupId;
        if (pid != null && pid !== '') {
            return String(pid);
        }
        const bid = block?.id;
        if (bid != null && bid !== '') {
            return String(bid);
        }
        const gid = block?.groupId;
        if (gid != null && gid !== '') {
            const gs = String(gid);
            if (!this.isPlaceholderAddonsV2GroupId(gs)) {
                return gs;
            }
        }
        if (gid != null && gid !== '') {
            return String(gid);
        }
        return this.generateUniqueId(6);
    }

    private extractAddonsV2OptionsFromLegacyGroup(
        legacyGroup: any,
        addonsV2Type: AddonsV2Type,
        canonicalGroupId: string,
    ): Array<{ id: string; text: string; cost: number; enabled?: boolean; productId?: string | null; imageUrl?: string; priceConfig?: Record<string, number> }> {
        const seed = canonicalGroupId || String(legacyGroup?.id ?? legacyGroup?.title ?? 'new-group');
        const stableId = (kind: 'choice' | 'option', index: number) => `${kind}-${seed}-${index}`;
        const optionalImageUrl = (obj: any): { imageUrl?: string } => {
            const url = typeof obj?.imageUrl === 'string' ? obj.imageUrl.trim() : '';
            return url ? { imageUrl: url } : {};
        };
        const optionalPriceConfig = (obj: any) => this.optionalAddonPriceConfig(obj);
        const resolveProductKey = (obj: any): string | null => {
            if (!this.hasAddonProductLink(obj)) {
                return null;
            }
            const pid = obj?.productId ?? obj?.linkedProduct;
            if (pid?.key) return String(pid.key);
            if (obj?.linkedProduct?.key) return String(obj.linkedProduct.key);
            if (obj?.productId?.key) return String(obj.productId.key);
            if (typeof obj?.productId === 'string' && obj.productId.trim() !== '') return obj.productId.trim();
            return null;
        };
        if (!Array.isArray(legacyGroup?.options)) {
            return [];
        }
        if (addonsV2Type === 'multiple') {
            const first = legacyGroup.options[0];
            const choices = Array.isArray(first?.choices) ? first.choices : [];
            if (choices.length > 0) {
                return choices.map((choice: any, choiceIndex: number) => {
                    const productId = resolveProductKey(choice);
                    return {
                        id: choice?.id || stableId('choice', choiceIndex),
                        text: choice?.text ?? '',
                        cost: Number.isFinite(Number(choice?.cost)) ? Number(choice.cost) : 0,
                        enabled: choice?.enabled !== false,
                        productId,
                        // linkedProduct: choice?.linkedProduct ? this.sanitizeProductReference(choice.linkedProduct) : null,
                        ...optionalImageUrl(choice),
                        ...optionalPriceConfig(choice),
                    };
                });
            }
            // Legacy often stores one radio group as several flat boolean rows (same optionGroupId), not nested choices.
            return legacyGroup.options.map((option: any, optionIndex: number) => {
                const productId = resolveProductKey(option);
                return {
                    id: option?.id || stableId('choice', optionIndex),
                    text: option?.text ?? '',
                    cost: Number.isFinite(Number(option?.cost)) ? Number(option.cost) : 0,
                    enabled: option?.enabled !== false,
                    productId,
                    // linkedProduct: option?.linkedProduct ? this.sanitizeProductReference(option.linkedProduct) : null,
                    ...optionalImageUrl(option),
                    ...optionalPriceConfig(option),
                };
            });
        }
        if (addonsV2Type === 'boolean') {
            const first = legacyGroup.options[0];
            if (
                legacyGroup.options.length === 1 &&
                Array.isArray(first?.choices) &&
                first.choices.length > 0
            ) {
                return first.choices.map((choice: any, choiceIndex: number) => {
                    const productId = resolveProductKey(choice);
                    return {
                        id: choice?.id || stableId('choice', choiceIndex),
                        text: choice?.text ?? '',
                        cost: Number.isFinite(Number(choice?.cost)) ? Number(choice.cost) : 0,
                        enabled: choice?.enabled !== false,
                        productId,
                        // linkedProduct: choice?.linkedProduct ? this.sanitizeProductReference(choice.linkedProduct) : null,
                        ...optionalImageUrl(choice),
                        ...optionalPriceConfig(choice),
                    };
                });
            }
        }
        return legacyGroup.options.map((option: any, optionIndex: number) => {
            const productId = resolveProductKey(option);
            return {
                id: option?.id || stableId('option', optionIndex),
                text: option?.text ?? '',
                cost: Number.isFinite(Number(option?.cost)) ? Number(option.cost) : 0,
                enabled: option?.enabled !== false,
                productId,
                // linkedProduct: option?.linkedProduct ? this.sanitizeProductReference(option.linkedProduct) : null,
                ...optionalImageUrl(option),
                ...optionalPriceConfig(option),
            };
        });
    }

    /**
     * Legacy option-group → addonsV2 option rows (same rules as sanitizeAddonsV2GroupForSave).
     * Editors call this instead of duplicating extraction logic.
     */
    extractAddonsV2OptionsFromLegacyShape(
        legacyOptionGroup: any,
        addonsV2Type: AddonsV2Type,
    ): Array<{ id: string; text: string; cost: number; enabled?: boolean; productId?: string | null; imageUrl?: string; priceConfig?: Record<string, number> }> {
        const canonicalGroupId = this.resolveAddonsV2GroupId(legacyOptionGroup?.addonsV2, legacyOptionGroup);
        return this.extractAddonsV2OptionsFromLegacyGroup(legacyOptionGroup, addonsV2Type, canonicalGroupId);
    }

    sanitizeAddonsV2GroupForSave(group: any, parentOptionGroup?: any): AddonsV2Group | null {
        if (!group || typeof group !== 'object') {
            return null;
        }
        const normalizedType: AddonsV2Type = group.type === 'multiple' ? 'multiple' : 'boolean';
        const canonicalGroupId = this.resolveAddonsV2GroupId(group, parentOptionGroup);
        const shell = parentOptionGroup ?? group;
        const extracted = this.extractAddonsV2OptionsFromLegacyGroup(shell, normalizedType, canonicalGroupId);
        const normalizedOptions =
            extracted.length > 0
                ? extracted.map((extOpt) => {
                      const match = Array.isArray(group?.options) && group.options.find(
                          (o: any) =>
                              o &&
                              (String(o.id) === String(extOpt.id) ||
                                  (o.productId != null &&
                                      extOpt.productId != null &&
                                      String(o.productId) === String(extOpt.productId)))
                      );
                      if (match) {
                          const pc = extOpt.priceConfig ?? match.priceConfig;
                          const url = extOpt.imageUrl ?? match.imageUrl;
                          return {
                              ...match,
                              ...extOpt,
                              ...(pc ? { priceConfig: pc } : {}),
                              ...(url ? { imageUrl: url } : {}),
                          };
                      }
                      return extOpt;
                  })
                : Array.isArray(group.options)
                  ? group.options.map((option: any) => {
                        const url =
                            typeof option?.imageUrl === 'string' ? option.imageUrl.trim() : '';
                        const pKey = option?.productId ? (typeof option.productId === 'object' ? option.productId.key : option.productId) : null;
                        return {
                            id: option?.id ?? this.generateUniqueId(4),
                            text: option?.text ?? '',
                            cost: Number.isFinite(Number(option?.cost)) ? Number(option.cost) : 0,
                            enabled: option?.enabled !== false,
                            productId: pKey,
                            // linkedProduct: option?.linkedProduct ? this.sanitizeProductReference(option.linkedProduct) : null,
                            ...(url ? { imageUrl: url } : {}),
                            ...this.optionalAddonPriceConfig(option),
                        };
                    })
                  : [];

        const dependencyRules = Array.isArray(group.showWhen?.all)
            ? group.showWhen.all
                  .filter((rule: any) => rule?.groupId != null && Array.isArray(rule?.optionIds) && rule.optionIds.length > 0)
                  .map((rule: any) => ({
                      groupId: String(rule.groupId),
                      operator: 'includes' as const,
                      optionIds: [...new Set(rule.optionIds.filter((id: any) => !!id).map((id: any) => String(id)))],
                  }))
            : [];

        let minSelections = Number.isFinite(Number(group.minSelections)) ? Math.max(0, Number(group.minSelections)) : 0;
        // Legacy payloads: required with min 0 still meant "pick at least one"
        if (group.required && minSelections < 1) {
            minSelections = 1;
        }
        const rawMaxSelections = Number.isFinite(Number(group.maxSelections)) ? Number(group.maxSelections) : null;
        const maxSelections = rawMaxSelections == null ? null : Math.max(minSelections, rawMaxSelections);
        const required = minSelections >= 1;

        const stepRaw = group.step;
        const step =
            stepRaw !== undefined && stepRaw !== null && String(stepRaw).trim() !== ''
                ? String(stepRaw)
                : null;

        // UI checkboxes live on the parent option group — prefer that over stale addonsV2.servingmethods.
        const rawSm = parentOptionGroup?.servingmethods ?? group?.servingmethods;
        const servingmethods = Array.isArray(rawSm)
            ? [...new Set(rawSm.filter((v: any) => typeof v === 'string' && ADDON_SERVING_METHOD_SET.has(v)))]
            : [];

        return {
            schemaVersion: 1,
            groupId: canonicalGroupId,
            title: group.title ?? '',
            description: group.description ?? '',
            step,
            type: normalizedType,
            required,
            minSelections,
            maxSelections: normalizedType === 'multiple' ? 1 : maxSelections,
            // Checkbox groups have no preselect in the UI — drop any mode left over from single-choice.
            preselectMode: normalizedType === 'boolean'
                ? 'none'
                : (group.preselectMode === 'specific' ? 'specific' : 'first'),
            preselectedOptionId: normalizedType === 'boolean' ? null : (group.preselectedOptionId ?? null),
            options: normalizedOptions,
            showWhen: dependencyRules.length > 0 ? { all: dependencyRules } : null,
            servingmethods,
        };
    }

    sanitizeProductAddonsV2ForSave(addonsV2: any, product?: any): AddonsV2ProductPayload | null {
        if (!addonsV2 || !Array.isArray(addonsV2.groups)) {
            return null;
        }
        const payload: AddonsV2ProductPayload = {
            groups: addonsV2.groups
                .map((grp: any) =>
                    this.sanitizeAddonsV2GroupForSave(
                        grp,
                        product ? this.inferLegacyOptionGroupShellFromProduct(product, grp) : undefined,
                    ),
                )
                .filter((grp: AddonsV2Group | null) => !!grp) as AddonsV2Group[],
        };
        if (product && payload.groups.length > 1) {
            const scratch = { ...product, addonsV2: payload };
            this.sortAddonsV2GroupsForProduct(scratch, this.groupOptionsByOptionGroupId(product, []));
            return scratch.addonsV2 ?? payload;
        }
        return payload;
    }

    resolveInitialBabStepValue(product: any): string | null {
        if (!product?.isUsingBuildABurger) {
            return null;
        }
        const steps = product.steps;
        if (Array.isArray(steps) && steps.length > 0) {
            const v = steps[0]?.value;
            if (v !== undefined && v !== null && String(v).trim() !== '') {
                return String(v);
            }
        }
        return 'type';
    }

    /** New product link from addon group editor (+): set initial BAB step on the V2 group when applicable. */
    prepareAddonsV2GroupForProductLink(product: any, groupTemplate: any): AddonsV2Group | null {
        const initialStep = this.resolveInitialBabStepValue(product);
        const draft =
            initialStep != null
                ? { ...(groupTemplate?.addonsV2 || groupTemplate), step: initialStep }
                : groupTemplate?.addonsV2 || groupTemplate;
        return this.sanitizeAddonsV2GroupForSave(draft, groupTemplate);
    }

    normalizeBabStepValue(step: string | number | null | undefined): string | null {
        if (step === undefined || step === null || String(step).trim() === '') {
            return null;
        }
        return String(step);
    }

    private findAddonsV2GroupIndexOnProduct(
        product: any,
        groupId: string | number,
        step?: string | number | null,
    ): number {
        const gid = String(groupId);
        const stepNorm = this.normalizeBabStepValue(step ?? null);
        const groups = Array.isArray(product?.addonsV2?.groups) ? product.addonsV2.groups : [];
        return groups.findIndex((item: AddonsV2Group) => {
            if (String(item?.groupId) !== gid) {
                return false;
            }
            return this.normalizeBabStepValue(item?.step ?? null) === stepNorm;
        });
    }

    productHasAddonsV2GroupOnStep(product: any, groupId: string | number, step: string | number): boolean {
        return this.findAddonsV2GroupIndexOnProduct(product, groupId, step) >= 0;
    }

    productHasAddonGroupOnOtherBabStep(
        product: any,
        groupId: string | number,
        step: string | number,
    ): boolean {
        const gid = String(groupId);
        const stepNorm = this.normalizeBabStepValue(step);
        if (!stepNorm) {
            return false;
        }
        const onOtherV2 = (product?.addonsV2?.groups || []).some(
            (g: AddonsV2Group) =>
                String(g.groupId) === gid && this.normalizeBabStepValue(g.step ?? null) !== stepNorm,
        );
        if (onOtherV2) {
            return true;
        }
        return (product?.options || []).some(
            (o: any) =>
                o?.optionGroupId != null &&
                String(o.optionGroupId) === gid &&
                this.normalizeBabStepValue(o?.step ?? null) !== stepNorm,
        );
    }

    readLinkedAddonsV2GroupStepOnProduct(product: any, groupId: string | number): string | null {
        const gid = String(groupId);
        const existing = Array.isArray(product?.addonsV2?.groups)
            ? product.addonsV2.groups.find((g: AddonsV2Group) => String(g.groupId) === gid)
            : undefined;
        if (existing?.step != null && String(existing.step).trim() !== '') {
            return String(existing.step);
        }
        if (Array.isArray(product?.options)) {
            for (const opt of product.options) {
                if (opt?.optionGroupId != null && String(opt.optionGroupId) === gid) {
                    const s = opt?.step;
                    if (s != null && String(s).trim() !== '') {
                        return String(s);
                    }
                }
            }
        }
        return null;
    }

    preserveAddonsV2GroupStepForProductSync(
        product: any,
        incoming: AddonsV2Group,
        stepHint?: string | number | null,
    ): AddonsV2Group {
        if (incoming.step != null && String(incoming.step).trim() !== '') {
            return incoming;
        }
        const hinted = this.normalizeBabStepValue(stepHint ?? null);
        if (hinted) {
            return { ...incoming, step: hinted };
        }
        const step = this.readLinkedAddonsV2GroupStepOnProduct(product, incoming.groupId);
        return step ? { ...incoming, step } : incoming;
    }

    syncAddonsV2GroupFromGlobalTemplateOnProduct(product: any, group: any): AddonsV2ProductPayload | null {
        const v2Group = this.sanitizeAddonsV2GroupForSave(group?.addonsV2 ?? group, group);
        if (!v2Group) {
            return null;
        }
        const gid = String(v2Group.groupId);
        const instances = (product?.addonsV2?.groups || []).filter(
            (g: AddonsV2Group) => String(g.groupId) === gid,
        );
        if (instances.length > 1) {
            let payload: AddonsV2ProductPayload = {
                groups: [...(product?.addonsV2?.groups || [])],
            };
            for (const inst of instances) {
                const merged = this.preserveAddonsV2GroupStepForProductSync(product, v2Group, inst.step);
                payload = this.upsertAddonsV2GroupOnProduct({ addonsV2: payload }, merged);
            }
            return payload;
        }
        const merged = this.preserveAddonsV2GroupStepForProductSync(product, v2Group);
        return this.upsertAddonsV2GroupOnProduct(product, merged);
    }

    upsertAddonsV2GroupOnProduct(product: any, group: AddonsV2Group): AddonsV2ProductPayload {
        const existingGroups = Array.isArray(product?.addonsV2?.groups) ? [...product.addonsV2.groups] : [];
        let idx = this.findAddonsV2GroupIndexOnProduct(product, group.groupId, group.step ?? null);
        const incomingStep = this.normalizeBabStepValue(group.step ?? null);
        // Upgrade a legacy null-step row instead of duplicating the same groupId on re-link.
        if (idx < 0 && incomingStep != null) {
            const orphanIdx = this.findAddonsV2GroupIndexOnProduct(product, group.groupId, null);
            if (orphanIdx >= 0) {
                idx = orphanIdx;
            }
        }
        if (idx >= 0) {
            const next = [...existingGroups];
            next[idx] = group;
            return { groups: next };
        }
        return { groups: [...existingGroups, group] };
    }

    /**
     * After drag-reorder in product add-ons UI, `product.options` order changes but clients read
     * `addonsV2.groups` order — align the latter with the on-screen group order.
     */
    reorderAddonsV2GroupsFromDisplayOrder(
        product: any,
        displayGroups: Array<{ optionGroupId?: string | number }>,
    ): void {
        const groups = product?.addonsV2?.groups;
        if (!Array.isArray(groups) || groups.length === 0 || !Array.isArray(displayGroups)) {
            return;
        }
        const orderedIds = displayGroups
            .map((g) => String(g?.optionGroupId ?? ''))
            .filter((id) => id !== '' && id !== 'unique');
        if (!orderedIds.length) {
            return;
        }
        const v2RowKey = (g: { groupId?: unknown; step?: unknown }) => {
            const step = this.normalizeBabStepValue(g?.step as string | null) ?? '';
            return `${String(g.groupId)}::${step}`;
        };
        const byKey = new Map<string, AddonsV2Group>(groups.map((g: AddonsV2Group) => [v2RowKey(g), g]));
        const displayKey = (g: { optionGroupId?: string | number; step?: unknown }) => {
            const step = this.normalizeBabStepValue(g?.step as string | null) ?? '';
            return `${String(g.optionGroupId)}::${step}`;
        };
        const orderedKeys = displayGroups
            .map((g) => displayKey(g))
            .filter((key) => key !== '::' && !key.startsWith('unique'));
        const seen = new Set<string>();
        const next: AddonsV2Group[] = [];
        for (const key of orderedKeys) {
            const g = byKey.get(key);
            if (g && !seen.has(key)) {
                next.push(g);
                seen.add(key);
            }
        }
        for (const g of groups) {
            const key = v2RowKey(g);
            if (!seen.has(key)) {
                next.push(g);
                seen.add(key);
            }
        }
        product.addonsV2 = { groups: next };
    }

    sortAddonsV2GroupsForProduct(
        product: any,
        displayGroups?: Array<{ optionGroupId?: string | number; step?: unknown }>,
    ): void {
        const groups = product?.addonsV2?.groups;
        if (!Array.isArray(groups) || groups.length <= 1) {
            return;
        }
        const display = Array.isArray(displayGroups) ? displayGroups : [];
        const stepOrder = (Array.isArray(product?.steps) ? product.steps : [])
            .map((s: { value?: unknown }) => this.normalizeBabStepValue(s?.value as string | null))
            .filter((v): v is string => v != null);
        const stepRank = new Map<string, number>();
        stepOrder.forEach((v, i) => stepRank.set(v, i));

        const displayIndex = new Map<string, number>();
        const displayKey = (g: { optionGroupId?: string | number; step?: unknown }) => {
            const step = this.normalizeBabStepValue(g?.step as string | null) ?? '';
            return `${String(g.optionGroupId)}::${step}`;
        };
        display.forEach((g, i) => {
            const key = displayKey(g);
            if (!displayIndex.has(key)) {
                displayIndex.set(key, i);
            }
        });

        const v2RowKey = (g: AddonsV2Group) => {
            const step = this.normalizeBabStepValue(g.step ?? null) ?? '';
            return `${String(g.groupId)}::${step}`;
        };
        const fallbackRank = stepOrder.length;

        const sorted = [...groups].sort((a, b) => {
            const stepA = this.normalizeBabStepValue(a.step ?? null) ?? '';
            const stepB = this.normalizeBabStepValue(b.step ?? null) ?? '';
            const rankA = stepRank.has(stepA) ? stepRank.get(stepA)! : fallbackRank;
            const rankB = stepRank.has(stepB) ? stepRank.get(stepB)! : fallbackRank;
            if (rankA !== rankB) {
                return rankA - rankB;
            }
            const idxA = displayIndex.get(v2RowKey(a)) ?? Number.MAX_SAFE_INTEGER;
            const idxB = displayIndex.get(v2RowKey(b)) ?? Number.MAX_SAFE_INTEGER;
            if (idxA !== idxB) {
                return idxA - idxB;
            }
            return v2RowKey(a).localeCompare(v2RowKey(b));
        });

        product.addonsV2 = { groups: sorted };
    }

    removeAddonsV2GroupFromProduct(
        product: any,
        groupId: string,
        step?: string | number | null,
    ): AddonsV2ProductPayload | null {
        const existingGroups = Array.isArray(product?.addonsV2?.groups) ? [...product.addonsV2.groups] : [];
        if (!existingGroups.length) {
            return null;
        }
        const gid = String(groupId);
        const stepNorm = this.normalizeBabStepValue(step ?? null);
        const groups = existingGroups.filter((item: AddonsV2Group) => {
            if (String(item?.groupId) !== gid) {
                return true;
            }
            if (stepNorm == null) {
                return false;
            }
            return this.normalizeBabStepValue(item?.step ?? null) !== stepNorm;
        });
        return groups.length ? { groups } : null;
    }

    /**
     * Migration policy gate:
     * - if product already has addonsV2 groups, keep writing addonsV2
     * - otherwise block addonsV2 writes while legacy addon rows still exist
     */
    canWriteAddonsV2OnProduct(product: any): boolean {
        if (Array.isArray(product?.addonsV2?.groups) && product.addonsV2.groups.length > 0) {
            return true;
        }
        const hasLegacyAddonRows = Array.isArray(product?.options)
            && product.options.some((o: any) => o?.optionGroupId != null);
        return !hasLegacyAddonRows;
    }

    groupOptionsByOptionGroupId(product, OptionGroupsData) {
        let groupedOptions = [];
        const v2Groups = product?.addonsV2?.groups;
        const v2TitleById = new Map<string, string>(
            (Array.isArray(v2Groups) ? v2Groups : []).map((v2: AddonsV2Group) => [
                String(v2.groupId),
                (v2.title || '').trim(),
            ]),
        );

        product?.options?.filter(option => option).forEach(option => {

            const ogIdRaw = option.optionGroupId ?? 'unique';
            const ogKey = ogIdRaw === 'unique' ? 'unique' : String(ogIdRaw);

            let group = groupedOptions.find(g =>
                String(g.optionGroupId ?? 'unique') === ogKey &&
                g.step === option.step &&
                !g.options.some(o => o.id === option.id)
            );

            if (!group) {
                const optionGroupId = option.optionGroupId || 'unique';
                const found = OptionGroupsData.find((opt) => String(opt.id) === String(optionGroupId));
                const v2Title = v2TitleById.get(String(optionGroupId)) || '';

                group = {
                    optionGroupId,
                    options: [],
                    title:
                        optionGroupId === 'unique'
                            ? ''
                            : (found?.title || v2Title || ''),
                    type: found?.type || option?.type || 'boolean',
                    step: option.step
                };

                groupedOptions.push(group);
            }

            group.options.push(option);
        });

        // V2-only products may have no legacy `options` rows. Synthesize display groups from addonsV2.
        if (Array.isArray(v2Groups) && v2Groups.length > 0) {
            v2Groups.forEach((v2: AddonsV2Group) => {
                const gid = String(v2.groupId);
                const v2Step = this.normalizeBabStepValue(v2.step ?? null);
                const existing = groupedOptions.find(
                    (g) =>
                        String(g.optionGroupId) === gid &&
                        this.normalizeBabStepValue(g.step ?? null) === v2Step,
                );
                if (existing) {
                    return;
                }
                const found = OptionGroupsData.find((opt) => String(opt.id) === gid);
                const choices = (v2.options || []).map((opt) => ({
                    id: opt.id,
                    text: opt.text,
                    cost: opt.cost,
                    enabled: opt.enabled !== false,
                    optionGroupId: gid,
                    type: v2.type,
                }));
                const syntheticOption = {
                    id: v2Step ? `${gid}-v2-${v2Step}` : `${gid}-v2`,
                    optionGroupId: gid,
                    type: v2.type,
                    text: v2.title || found?.title || '',
                    title: v2.title || found?.title || '',
                    cost: 0,
                    // Keep same UI contract as legacy multiple rows: details are under choices[].
                    choices,
                };
                groupedOptions.push({
                    optionGroupId: gid,
                    options: [syntheticOption],
                    title: found?.title || v2.title || '',
                    type: found?.type || v2.type || 'boolean',
                    step: v2.step ?? undefined,
                });
            });
        }

        return groupedOptions.filter(group => Array.isArray(group.options) && group.options.length > 0);
    }

    /** Persist BAB step on an addonsV2 group already linked to this product. */
    setAddonsV2GroupStepOnProduct(product: any, groupId: string, step: string | null | undefined): void {
        if (!product?.addonsV2?.groups?.length) return;
        const gid = String(groupId);
        const idx = product.addonsV2.groups.findIndex((g: AddonsV2Group) => String(g.groupId) === gid);
        if (idx < 0) return;
        const nextStep =
            step !== undefined && step !== null && String(step).trim() !== '' ? String(step) : null;
        const current = product.addonsV2.groups[idx];
        const updated = this.sanitizeAddonsV2GroupForSave(
            { ...current, step: nextStep },
            this.inferLegacyOptionGroupShellFromProduct(product, current),
        );
        if (updated) {
            product.addonsV2.groups = [...product.addonsV2.groups];
            product.addonsV2.groups[idx] = updated;
        }
    }

    /** Apply step to legacy option rows for one linked group (step-by-step products). */
    applyStepToLegacyOptionsForGroup(product: any, groupId: string, step: string | null | undefined): void {
        if (!Array.isArray(product?.options)) return;
        const gid = String(groupId);
        const nextStep =
            step !== undefined && step !== null && String(step).trim() !== '' ? String(step) : undefined;
        for (const option of product.options) {
            if (option?.optionGroupId != null && String(option.optionGroupId) === gid) {
                if (nextStep !== undefined) {
                    option.step = nextStep;
                } else {
                    delete option.step;
                }
            }
        }
    }

    productHasLinkedAddonGroup(product: any, groupId: string | number): boolean {
        const gid = String(groupId);
        if (product?.optionGroupIds?.[gid]) {
            return true;
        }
        if (Array.isArray(product?.addonsV2?.groups)) {
            return product.addonsV2.groups.some(
                (g: AddonsV2Group) => String(g.groupId) === gid,
            );
        }
        return false;
    }

    checkAvailableOptionGroups(product, data) {
        let optionGroupNames: any[] = [];
        let availableOptions: any = data;
        if (product.options) {
            const addedKeys = new Set();
            const check = product.options.filter(option => {
                if (option?.optionGroupId) {
                    if (!addedKeys.has(option.optionGroupId)) {
                        addedKeys.add(option.optionGroupId);
                        const data1 = data.find(group => group.id === option.optionGroupId);
                        if (data1) {
                            if (data1.title) {
                                option.title = data1.title;
                            }
                            if (data1.type) {
                                option.type = option.type || data1.type;
                            }
                        }
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return true;
                }
            });

            optionGroupNames = check;
        }

        const notYetLinked = (group: any) =>
            group?.id != null && !this.productHasLinkedAddonGroup(product, group.id);

        if (product.isUsingBuildABurger || product.optionGroupIds) {
            availableOptions = data.filter(notYetLinked);
        } else {
            availableOptions = data;
        }

        return { optionGroupNames, availableOptions };
    }

    toAssociatedProduct(product: any): AssociatedProduct | null {
        const id = product?.key ?? product?.id;
        if (id == null || id === '') {
            return null;
        }
        return {
            id: String(id),
            name: String(product?.Name ?? product?.name ?? ''),
            price: Number(product?.Cost ?? product?.price ?? 0),
            image: String(product?.ImageUrl ?? product?.image ?? ''),
            enabled: product?.enabled !== false,
            description: String(product?.Description ?? product?.description ?? ''),
        };
    }

    rebuildAssociatedProductsFromSelection(group: any, products: any[]): void {
        group.associatedProducts = (products ?? [])
            .map((product) => this.toAssociatedProduct(product))
            .filter((item): item is AssociatedProduct => item != null);
    }

    removeFromAssociatedProducts(group: any, productId: string | number): void {
        if (!Array.isArray(group?.associatedProducts)) {
            group.associatedProducts = [];
            return;
        }
        const id = String(productId);
        group.associatedProducts = group.associatedProducts.filter((item) => String(item.id) !== id);
    }

    /** Keep addon group associatedProducts in sync with product link state. */
    syncAssociatedProductOnGroup(group: any, product: any, groupId: string | number): void {
        if (!group) {
            return;
        }
        if (this.productHasLinkedAddonGroup(product, groupId)) {
            const snapshot = this.toAssociatedProduct(product);
            if (!snapshot) {
                return;
            }
            if (!Array.isArray(group.associatedProducts)) {
                group.associatedProducts = [];
            }
            const idx = group.associatedProducts.findIndex((item) => String(item.id) === snapshot.id);
            if (idx >= 0) {
                group.associatedProducts[idx] = snapshot;
            } else {
                group.associatedProducts.push(snapshot);
            }
            return;
        }
        const productId = product?.key ?? product?.id;
        if (productId != null) {
            this.removeFromAssociatedProducts(group, productId);
        }
    }

    unlinkProductFromAddonGroup(product: any, groupId: string | number): void {
        const gid = String(groupId);
        if (product?.optionGroupIds?.[gid]) {
            delete product.optionGroupIds[gid];
        }
        if (Array.isArray(product?.options)) {
            for (let index = product.options.length - 1; index >= 0; index--) {
                const option = product.options[index];
                if (option?.optionGroupId != null && String(option.optionGroupId) === gid) {
                    product.options.splice(index, 1);
                }
            }
        }
        product.addonsV2 = this.removeAddonsV2GroupFromProduct(product, gid);
        this.updateProduct(this.sanitizeProductForAddonSave(product));
    }

    async persistOptionGroup(group: any, routeName: string): Promise<any> {
        if (!group?.id || !routeName) {
            return group;
        }
        const url = `${environment.pubQueueApiUrlEU}/v1/units/optiongroups/${group.id}`;
        const payload = { ...group, routeName };
        return await this.http.post(url, payload).toPromise();
    }

    /** Update linked addon groups only when associatedProducts display fields changed. */
    async syncAssociatedProductsForSavedProduct(product: any, routeName: string): Promise<void> {
        const snapshot = this.toAssociatedProduct(product);
        if (!snapshot || !routeName) {
            return;
        }
        const groups = await this.getOptionGroups(routeName);
        if (!Array.isArray(groups)) {
            return;
        }
        const saves: Promise<any>[] = [];
        for (const group of groups) {
            const list = group?.associatedProducts;
            if (!Array.isArray(list)) {
                continue;
            }
            const idx = list.findIndex((item) => String(item.id) === snapshot.id);
            if (idx < 0 || !this.associatedProductChanged(list[idx], snapshot)) {
                continue;
            }
            list[idx] = snapshot;
            saves.push(this.persistOptionGroup(group, routeName));
        }
        if (saves.length) {
            await Promise.all(saves);
        }
    }

    associatedProductDisplayFieldsChanged(before: any, after: any): boolean {
        const a = this.toAssociatedProduct(before);
        const b = this.toAssociatedProduct(after);
        if (!a || !b) {
            return false;
        }
        return this.associatedProductChanged(a, b);
    }

    private associatedProductChanged(a: AssociatedProduct, b: AssociatedProduct): boolean {
        return String(a.id) !== String(b.id)
            || String(a.name ?? '') !== String(b.name ?? '')
            || Number(a.price ?? 0) !== Number(b.price ?? 0)
            || String(a.image ?? '') !== String(b.image ?? '')
            || (a.enabled !== false) !== (b.enabled !== false)
            || String(a.description ?? '') !== String(b.description ?? '');
    }

    async getOptionGroups(routeName: string) {
        const url = `${environment.pubQueueApiUrlEU}/v1/units/optiongroups?unit=${routeName}`;
        return await this.http.get<any>(url).toPromise();
    }

    getAllergenCategoryTypes(): string[] {
        return [...this.allergenCategoryTypes];
    }

    async getIngredientLibrary(routeName: string): Promise<IIngredient[]> {
        const snapshot = await this.afDatabase.database.ref(`Clients/${routeName}/ingredientLibrary`).once("value");
        const val = snapshot.val();
        if (!val) return [];
        return Object.keys(val).map(key => ({
            key,
            ...val[key]
        }));
    }

    async saveIngredientToLibrary(routeName: string, ingredient: IIngredient): Promise<void> {
        if (!ingredient.key) {
            const ref = this.afDatabase.database.ref(`Clients/${routeName}/ingredientLibrary`).push();
            ingredient.key = ref.key!;
        }
        await this.afDatabase.database.ref(`Clients/${routeName}/ingredientLibrary/${ingredient.key}`).set(ingredient);
    }

    async deleteIngredientFromLibrary(routeName: string, key: string): Promise<void> {
        await this.afDatabase.database.ref(`Clients/${routeName}/ingredientLibrary/${key}`).set(null);
    }

    async syncIngredientLibraryAssociations(routeName: string, productKey: string, selectedIngredients: IIngredient[], library?: IIngredient[]): Promise<void> {
        const lib = library || await this.getIngredientLibrary(routeName);
        if (!lib || lib.length === 0) return;

        const selectedKeys = new Set((selectedIngredients || []).map(ing => ing.key));

        await Promise.all(lib.map(async (ing) => {
            let linked = ing.linkedProducts || [];
            const isCurrentlySelected = selectedKeys.has(ing.key);
            const isLinked = linked.includes(productKey);

            let changed = false;
            if (isCurrentlySelected && !isLinked) {
                linked.push(productKey);
                changed = true;
            } else if (!isCurrentlySelected && isLinked) {
                linked = linked.filter(key => key !== productKey);
                changed = true;
            }

            if (changed) {
                ing.linkedProducts = linked;
                await this.saveIngredientToLibrary(routeName, ing);
            }
        }));
    }

    async deleteIngredientAndRemoveFromProducts(routeName: string, ingredient: IIngredient): Promise<void> {
        await this.deleteIngredientFromLibrary(routeName, ingredient.key);

        const productKeys = ingredient.linkedProducts || [];
        if (productKeys.length === 0) return;

        await Promise.all(productKeys.map(async (prodKey) => {
            const productRef = this.afDatabase.database.ref(`Clients/${routeName}/menuItems/${prodKey}`);
            const snapshot = await productRef.once("value");
            const product = snapshot.val();
            if (product && product.ingredients) {
                const updatedIngredients = (product.ingredients as IIngredient[])
                    .filter(ing => ing.key !== ingredient.key)
                    .map(ing => ({ key: ing.key, name: ing.name }));
                await productRef.child("ingredients").set(updatedIngredients);
            }
        }));
    }

}


export interface IMenu {
    key: string;
    isActive: boolean;
    name: string;
    categories: IMenuCategory[];
    timeSchedule: ITimeSchedule;
    deliveryOptions?: any;
    featuredProductsList?: string[];
}

export interface ITimeSchedule {
    weekdays: any[];
    activationTime: string;
    active: boolean;
}


export interface IMenuCategory {
    key?:string;
    name: string;
    type?: "Mat" | "Dryck";
    items: IMenuItem[];
    isGlobal?:string;
    description:string;
    imageUrl?: string;
}

export interface IIngredient {
    key: string;
    name: string;
    linkedProducts?: string[];
}

export interface IMenuItem {
    key: string;
    Category?: string;

    Name: string;
    NameInternal: string;
    Description: string;
    DescriptionLong: string;
    Cost: number;
    basePrice?: number;
    /** Selling mode. `"hg"` = price is per 100 g; guest/POS selects weight. */
    unitType?: string | null;
    minWeightGrams?: number | null;
    maxWeightGrams?: number | null;
    weightStepGrams?: number | null;
    Volume: number;
    ImageUrl: string;
    blurHash?: string;
    tags?: string[];
    isUsingBuildABurger?: boolean;
    disableNameProduct?: boolean;
    steps?: any[];
    options?: IMenuItemOption[];
    /** Addons V2 payload, stored as grouped rules-based addon definitions. */
    addonsV2?: AddonsV2ProductPayload;
    ingredients?: IIngredient[];
    vatInfo: {
        articleAccountId: string, articleAccountings?: { percent: number, id: string }[],
        exceptions?: { vatInfo: { articleAccountId: string }, id: string, reason: string }[]
    };
    type: { productTypeId: number, containsAlcohol: boolean, bongCategoryType?: ProductTypeChild, productCategoryType?: ProductTypeChild, allergenCategories?: [] };
    purchaseCost?: number
    externalOrigin?: { key: string, externalId: string, vatPercentage?: number };
    preferredMenuCategory?: string;
    useStockBalance: boolean;
    showStockBalance: boolean;
    enabled: boolean;
    disabledReason: string;
    stockBalance: number;
    isCombinedProduct: boolean;
    combinedVatInfo: { productId: string, percent: number }[];
    reusablePackaging?: any;
    grinItem?: boolean;
    optionGroupIds?: any;
    priceConfig?: any;
    displayConfig?: any;
    dateCreated?: Date;
    dateEdited?: Date;
    featured?:boolean;
    /** When true, product appears in the weekly menu planner product register (indexed query). */
    isWeeklyMenuProduct?: boolean;
}
export interface IWeeklyMenuProductEntry {
    sortOrder: number;
    productKey: string;
    available: boolean;
}

export interface IWeeklyMenuDay {
    closed: boolean;
    cutoffTime: string;
    availableFrom: string;
    availableUntil: string;
    products: Record<string, IWeeklyMenuProductEntry>;
}

export interface IWeeklyMenuGlobalSettings {
    orderDaysInAdvance: number;
}

export interface IWeeklyMenuServingOptionToggle {
    enabled: boolean;
}

export interface IWeeklyMenuFixedDeliveryLocation {
    name: string;
    coupon?: any;
}

export interface IWeeklyMenuCouponOption {
    key: string;
    code: string;
}

export interface IWeeklyMenuServingOptions {
    // enabled: boolean;
    takeaway: IWeeklyMenuServingOptionToggle;
    eatin: IWeeklyMenuServingOptionToggle;
    deliveryToFixedLocations: IWeeklyMenuServingOptionToggle & {
        minimumFee?: number;
        locations?: IWeeklyMenuFixedDeliveryLocation[];
    };
}

export type WeeklyMenuSlotId = 'current' | 'next';

export interface IWeeklyMenuWeekInfo {
    weekKey: string;
    weekStartDate: string;
    weekEndDate: string;
}

export interface IWeeklyMenuSlot {
    weekKey?: string;
    weekStartDate?: string;
    weekEndDate?: string;
    globalSettings: IWeeklyMenuGlobalSettings;
    /** @deprecated Prefer `settings.servingOptions`; omitted when persisting cloned/reset week nodes. */
    servingOptions?: IWeeklyMenuServingOptions;
    weekdays: Record<string, IWeeklyMenuDay>;
}

export interface IWeeklyMenuRolloverSettings {
    enabled: boolean;
    cutoffDayIndex: number;
    cutoffTime: string;
}

export interface IWeeklyMenuSettings {
    rollover: IWeeklyMenuRolloverSettings;
    timezone?: string;
    /** Single source of truth for takeaway / eat-in / fixed delivery (shared by all ISO week planners). */
    servingOptions?: IWeeklyMenuServingOptions;
}

export interface IWeeklyMenus {
    settings: IWeeklyMenuSettings;
    weeks: Record<string, IWeeklyMenuSlot>;
}

export interface ProductTypeChild {
    id: string;
    productTypeId: string;
    label: string;
}
export interface IMenuItemOption {
    choices: IOptionChoice[];
    cost: number;
    text: string;
    type: string;
    step?: string
    optionGroupId?: string;
    visibleForMenu?: string;
    /** Which serving channels this addon option applies to (e.g. delivery, takeaway). */
    servingmethods?: string[];
    linkedProduct?: IMenuItem;
    /** Optional image shown on kiosk addon cards only. */
    imageUrl?: string;
}

export type AddonsV2Type = 'multiple' | 'boolean';
export type AddonsV2PreselectMode = 'none' | 'first' | 'specific';

export interface AddonsV2DependencyRule {
    groupId: string;
    operator: 'includes';
    optionIds: string[];
}

export interface AddonsV2Option {
    id: string;
    text: string;
    cost: number;
    /** Per serving method, same keys as product `priceConfig` (`"0"`, `"-1"`, `"-2"`, `"-3"`). */
    priceConfig?: Record<string, number>;
    enabled?: boolean;
    productId?: string | null;
    imageUrl?: string;
}

/** Canonical Addons V2 group schema shared across admin and consuming clients. */
export interface AddonsV2Group {
    schemaVersion: 1;
    groupId: string;
    title: string;
    description?: string;
    /** Build-a-burger / step-by-step: matches `product.options[].step` and client BAB step keys. */
    step?: string | null;
    /** Keep legacy naming for compatibility: multiple(select one), boolean(checkbox). */
    type: AddonsV2Type;
    required: boolean;
    minSelections: number;
    maxSelections: number | null;
    preselectMode: AddonsV2PreselectMode;
    preselectedOptionId: string | null;
    options: AddonsV2Option[];
    showWhen: { all: AddonsV2DependencyRule[] } | null;
    servingmethods?: string[];
}

export interface AddonsV2ProductPayload {
    groups: AddonsV2Group[];
}

/** Snapshot stored on addon group JsonData for linked product display. */
export interface AssociatedProduct {
    id: string;
    name: string;
    price: number;
    image: string;
    enabled: boolean;
    description?: string;
}
export interface IOptionChoice {
    cost: number;
    text: string;
    linkedProduct: IMenuItem;
    imageUrl?: string;
    priceConfig?: Record<string, number>;
}

export class ProductType {
    id: number;
    name: string;
}

export class Column {
    field: string;
    header: string;
}

export class CartItem implements IMenuItem {
    constructor(init?: Partial<CartItem>) {
        Object.assign(this, init);
    }
    basePrice?: number;
    blurHash?: string;
    isUsingBuildABurger?: boolean;
    disableNameProduct?: boolean;
    steps?: any[];
    purchaseCost?: number;
    externalOrigin?: { key: string; externalId: string; vatPercentage?: number; };
    preferredMenuCategory?: string;
    useStockBalance: boolean;
    showStockBalance: boolean;
    enabled: boolean;
    disabledReason: string;
    stockBalance: number;
    reusablePackaging?: any;
    grinItem?: boolean;
    optionGroupIds?: any;
    priceConfig?: any;
    displayConfig?: any;
    dateCreated?: Date;
    dateEdited?: Date;
    key: string;
    Category: string;
    Name: string;
    NameInternal: string;
    Description: string;
    DescriptionLong: string;
    Cost: number;
    Volume: number;
    ImageUrl: string;
    coupon: Coupon;
    tags: string[];
    options: IMenuItemOption[];
    ingredients?: IIngredient[];
    vatInfo: { articleAccountId: string; vatCategoryId: string };
    type: {
        productTypeId: number;
        containsAlcohol: boolean;
        bongCategoryType?: ProductTypeChild;
        productCategoryType?: ProductTypeChild;
        allergenCategories?: [];
    };
    quantity: number;
    NrOfItems: number;
    removeFromRecipe?: any[];
    getTypeText = () => {
        if (!this.type) throw `NO type is set on ${this.Name}`;
        switch (this.type.productTypeId) {
            case 1:
                return "food";
            case 2:
                return "drink";
            case 3:
                return "freeText";
        }
    };
    isCombinedProduct: boolean;
    combinedVatInfo: { productId: string, percent: number }[];
    origin: string;
}
